/**
 * Practices Section Controller
 * Manages the Experiments page UI with list/detail slide pattern.
 *
 * The page is one destination with two VIEWS behind the shared page-toggle
 * pill bar (`_renderNavBar`, same component /discovery, /library and /partner
 * use): **Things to try** — open experiments, one row each — and
 * **Findings** — closed experiments, led by what they taught. Rows the user
 * archived are reached from the filter bar's Show filter inside Things to
 * try, not from a footer and not from a third destination.
 *
 * An accepted experiment stays in Things to try until the USER closes it,
 * archives it or deletes it. Nothing moves on a clock: a passed window turns
 * the row's chip from "In progress" to "Overdue" and changes nothing else.
 *
 * UI vocabulary is experiments/findings; internal naming (route, classes,
 * `practices` ids, API surface) is unchanged.
 *
 * One fetch loads every non-draft status (`status: 'all'`); switching views or
 * filters is a local partition, never a refetch.
 */

import page from '../vendor/page.js';
import { escapeHtml, eventBus } from '../core/utils.js';
import { StickyStack } from '../utils/StickyStack.js';
import { ResultsFilterBar } from '../components/ResultsFilterBar.js';
import { renderEmptyState } from '../components/emptyState.js';
import { renderNavBar as renderSegmentNavBar, renderNavItem as renderSegmentNavItem } from '../components/segmentNav.js';
import { buildPageInfoPanel, renderPageInfoTitle, renderPageInfoFlow, renderPageInfoFaq } from '../components/pageInfoPanel.js';
import { Tooltip } from '../utils/Tooltip.js';
import { API } from '../core/api.js';
import { openSessionInPanel } from '../components/sessionDrillHelper.js';
import { practiceStatusInfo, daysUntilWindowClose } from '../components/practiceDrillHelper.js';
import { launchPracticeBuild } from '../orchestration/practiceBuildLauncher.js';
import { iconArrowUpRight } from '../utils/icons.js';
import { iconIdForItem, iconSizeForItem } from '../components/searchSources.js';
import { notifyArchived, notifyArchiveFailed } from '../utils/archiveFeedback.js';
import { renderRowKebab, mountSelectToolbar, RowMenuController } from '../components/optionsMenu.js';
import { buildTakeawayBox, takeawayBoxHtml, toggleTakeawayBox } from '../components/takeawayBox.js';
import { renderDisclosure, wireDisclosures, zoneWrapHtml } from '../components/disclosure.js';
import {
  PREDICTION_BLOCK_LABEL, PREDICTION_BLOCK_LABEL_PAST,
  NOTICE_SUBLABEL, NOTICE_SUBLABEL_PAST,
  VERDICT_BANDS
} from '../components/predictionBlock.js';
import { modalService } from '../services/ModalService.js';
import {
  emitContentViewed,
  emitContentStarred,
  emitContentArchived,
  emitContentDeleted,
  CONTENT_VIEWED,
  CONTENT_STARRED,
  CONTENT_ARCHIVED,
  CONTENT_DELETED
} from '../core/contentSync.js';

/** Filled star marker — shared sprite symbol. */
const STAR_SVG = `<svg width="15" height="15" viewBox="0 0 24 24" aria-hidden="true"><use href="#icon-star"/></svg>`;

/**
 * Placeholder copy per view. Each key is a partition of one loaded list, so an
 * empty list is always a no-data state for THAT partition — the Show filter
 * swaps which copy speaks rather than hiding rows behind a generic "no results".
 */
const EMPTY_COPY = {
  open: {
    title: 'No experiments in progress',
    description: 'Build one below — something small to try, one thing to watch for, and a window to run it in.',
    // Same action token as the mobile fixed button, so both reach
    // `handleAddPractice` through the container's delegated handler.
    action: { label: 'Build an experiment', dataAction: 'add-practice' }
  },
  archived: {
    title: 'Nothing archived',
    description: 'Experiments you step away from land here. Nothing is deleted — you can restore any of them.'
  },
  findings: {
    title: 'No findings yet',
    description: 'When you report back on an experiment and close it, what it taught you lands here.'
  }
};

/**
 * How a report landed against the experiment's prediction (`PracticeLog.
 * predictionVerdict`). `tone` selects the colour via the shared
 * `.practice-verdict--*` map in features/practices.css, which every verdict
 * surface reads — today that is the Finding detail plate's dot and its lede
 * rule. One source, so a verdict can never read as two different colours in
 * two places.
 *
 * `tone` names the STATE, not the hue. An earlier version keyed this on
 * colour words (`blue`, `orange`, `green`) and the names went stale the first
 * time the palette moved; a state name survives a recolour.
 *
 * The map doubles as the VOCABULARY: `_latestVerdict` treats key membership as
 * the validity test, so an unrecognised stored value reads as absent rather
 * than colouring a plate with a meaning nothing defines. `unclear` and an
 * ABSENT verdict stay different states — `unclear` says the window ran and the
 * signal was ambiguous, absence says nothing was recorded — and each has its
 * own band copy (`VERDICT_BANDS`, predictionBlock.js).
 *
 * There is no short label here. The two surfaces that needed one — the
 * Findings row's verdict chip and the bar's Outcome menu — are gone: the
 * finding row states its conclusion in the model's own words, and the verdict
 * word survives only as the Activity/lineage qualifier tag (`VERDICT_WORDS`).
 */
const VERDICT_CHIPS = {
  didnt_happen: { tone: 'disconfirmed' },
  partly: { tone: 'partial' },
  happened: { tone: 'confirmed' },
  unclear: { tone: 'unclear' }
};

/**
 * Short-form verdict word — the qualifier tag on Activity's report rows and on
 * the lineage rows beside them. Lowercase because those tags sit beside
 * `running`, `archived` and `kept open`, which are status words rather than
 * labels. "Held", not "Held up": in a sentence "held up" is unambiguous, but
 * as a bare tag it reads for a beat as *delayed*.
 *
 * The only place this page states a verdict as a WORD. The detail plate states
 * it as a full sentence (`VERDICT_BANDS`); the Findings row states what was
 * learned instead.
 */
const VERDICT_WORDS = {
  didnt_happen: "didn't hold",
  partly: 'partly held',
  happened: 'held',
  unclear: 'unclear'
};

/**
 * `?status=` values ↔ page state. The param is kept (deep links and bookmarks
 * predate the redesign): `active` and `completed` are the two segments,
 * `archived` resolves to the Things to try segment with its Show filter set to
 * Archived.
 */
const STATUS_TO_STATE = {
  active: { view: 'open', listFilter: 'active' },
  completed: { view: 'findings', listFilter: 'active' },
  archived: { view: 'open', listFilter: 'archived' }
};

/**
 * `PracticeLog.outcome` values that mark a CONFIRMED close (the check-in was
 * completed, not declined). Counterpart of `CHECKIN_TAKEAWAY_OUTCOMES`
 * (`backend/src/constants/practices.js`) — the frontend can't import from the
 * backend, so this list must be kept in sync with it by hand. A declined
 * close leaves `outcome: null` on the log row while `takeawayJson` is still
 * fully populated, so this set is what distinguishes an established finding
 * from learning-so-far (see `_findingTakeawayLog`).
 */
const CONFIRMED_TAKEAWAY_OUTCOMES = ['mastered', 'outgrown', 'inconclusive'];

/**
 * Leading-slot type icon. A running or archived experiment carries the
 * checkbox (the sidebar Practices glyph); a Finding carries the lightbulb —
 * the same per-item rule sidebar recents and /search use (`iconIdForItem`).
 * Reuses the shared session-card dot so unread state matches other nav cards.
 * @param {boolean} unread
 * @param {Object} practice
 * @returns {string}
 */
function practiceIconHtml(unread, practice) {
  const dot = unread
    ? '<span class="session-card__type-icon-dot" aria-hidden="true"></span>'
    : '';
  const item = { type: 'practice', payload: practice };
  const size = iconSizeForItem(item);
  return `<span class="session-card__type-icon" aria-hidden="true"><svg width="${size}" height="${size}"><use href="#icon-${iconIdForItem(item)}"/></svg>${dot}</span>`;
}

export class PracticesSection {
  /**
   * URL parameter schema for deep linking and view state.
   *
   * `status` names the view: `active` → Things to try, `completed` → Findings,
   * `archived` → Things to try filtered to Archived (the retired standalone
   * "Archived" destination's deep links land there rather than 404-ing).
   * `practice` is the per-user `practiceNumber` (Int) — a stable,
   * human-readable identifier persisted on Practice. NOT the row UUID.
   * `journey` is accepted as a legacy alias for bookmarks from before the
   * practice-domain collapse; reads only, no writes.
   */
  static urlParamSchema = {
    status: {
      type: 'enum',
      enum: ['active', 'completed', 'archived'],
      default: 'active'
    },
    practice: {
      type: 'string',
      validate: (v) => /^\d+$/.test(v) && parseInt(v, 10) >= 1
    },
    journey: {
      type: 'string',
      validate: (v) => /^\d+$/.test(v) && parseInt(v, 10) >= 1
    }
  };

  constructor(elements, eventBus) {
    this.container = elements.container;
    this.eventBus = eventBus;

    this.isActive = false;
    /** @type {'open'|'findings'} Active segment. */
    this.view = 'open';
    /** @type {'active'|'archived'} Things to try's Show filter. */
    this.listFilter = 'active';
    this.sortOrder = 'newest';

    this.allPractices = [];

    this.isLoading = false;
    this.loadError = null;

    this.filterBar = null;

    this.currentPractice = null;
    this._panelOpen = false;
    this._detailWrapper = null;

    // Select mode (multi-select bulk actions)
    this.selectMode = false;
    this.selectedIds = new Set();
    /** Row options menu open/close controller (menu is a child of its `.practice-card`). */
    this._rowMenu = new RowMenuController({
      container: () => this.container,
      keyAttr: 'data-practice-id',
      rowSelector: '.practice-card',
      hoverClose: true,
    });
    this._actionInFlight = false;
    this.actionNotice = '';

    this.stickyStack = null;

    this.eventBus?.on?.('badge:state-changed', () => {
      if (this.isActive) this.updateContentOnly();
    });

    this.initialize();
  }

  static PAGE_INFO_TABS = {
    overview: {
      label: 'Overview',
      content: `
        <h4 class="page-info-detail__subtitle">Small Tries, Real Data</h4>
        <p class="page-info-detail__text">
          An experiment is one concrete thing to try, with one thing to
          watch for while you do it. Some also carry a prediction — what the
          old pattern claims will happen. You run it once, and find out.
        </p>
        ${renderPageInfoFlow({
          steps: [
            {
              label: 'Build it',
              detail: `One specific try, designed around your situation, with
                what to watch for written down.`,
            },
            {
              label: 'Run it',
              detail: 'One observation window — a real chance to try it in life.',
            },
            {
              label: 'Report back',
              detail: `A short conversation about what happened and what
                you noticed.`,
            },
            {
              label: 'It becomes a finding',
              detail: 'The experiment closes into what it taught you.',
            },
          ],
        })}
        ${renderPageInfoFaq([
          {
            q: 'Does anything expire?',
            a: `No. An experiment stays in your list until you close it or
              archive it — nothing leaves on its own.`,
          },
          {
            q: 'Where do experiments come from?',
            a: `Out of your conversations, designed around your situation so
              the try stays specific rather than generic — or ask to build
              one any time.`,
          },
          {
            q: 'What if the window passes and I never got the chance?',
            a: `Keep it open — the window moves. An experiment only closes
              when you report back.`,
          },
        ])}
      `,
    },
    checkins: {
      label: 'Reporting back',
      content: `
        <h4 class="page-info-detail__subtitle">The Conversation After</h4>
        <p class="page-info-detail__text">
          Report back once you've tried it. It's a short conversation, not a
          form: you say what happened, and your therapist helps you work out
          what it showed.
        </p>
        ${renderPageInfoFaq([
          {
            q: 'What if I didn’t get a real chance to try?',
            a: `Say so — the experiment stays open and the window moves.
              Otherwise it closes into a finding.`,
          },
          {
            q: 'What happens after I report back?',
            a: `The experiment closes into a finding, led by what it taught
              you — and, when it carried a prediction, what happened to it.`,
          },
        ])}
      `,
    },
    status: {
      label: 'Things to try & Findings',
      content: `
        <h4 class="page-info-detail__subtitle">Where Things Live</h4>
        <p class="page-info-detail__text">
          Things to try holds the experiments still running. Findings holds the
          ones you've closed, led by what each one taught you.
        </p>
        ${renderPageInfoFaq([
          {
            q: 'What does Archived mean?',
            a: `It's a filter on Things to try, holding the experiments you
              stepped away from. You can restore any of them.`,
          },
          {
            q: 'Is anything ever deleted?',
            a: `No. Closed experiments become findings, and archived ones can
              be restored any time.`,
          },
        ])}
      `,
    },
  };

  initialize() {
    eventBus.on('right-panel:closed', (data) => {
      const isOurPanel = data?.contentType === 'practice-detail' || data?.contentType === 'practice-info';
      if (!isOurPanel || !this.isActive) return;

      this._clearDetailSelection();
      this._panelOpen = false;
      this.updateContentOnly();

      const url = new URL(window.location.href);
      let dirty = false;
      if (url.searchParams.has('practice')) { url.searchParams.delete('practice'); dirty = true; }
      if (url.searchParams.has('journey')) { url.searchParams.delete('journey'); dirty = true; }
      if (dirty) page.replace(url.pathname + (url.search ? url.search : ''));
    });

    eventBus.on(CONTENT_VIEWED, (data = {}) => {
      const { itemType, itemId, viewed } = data || {};
      if (itemType !== 'practice' || (viewed !== true && viewed !== false)) return;
      const viewedAt = viewed ? new Date().toISOString() : null;
      const practice = this.allPractices.find((item) => String(item?.id) === String(itemId));
      const current = String(this.currentPractice?.id) === String(itemId)
        ? this.currentPractice
        : null;
      if ((!practice || Boolean(practice.viewedAt) === viewed)
          && (!current || Boolean(current.viewedAt) === viewed)) return;
      if (practice) practice.viewedAt = viewedAt;
      if (current) current.viewedAt = viewedAt;
      if (this.isActive) this.updateContentOnly();
    });

    // Keep the active /practices list synchronized when a practice lifecycle
    // action originates from its sidebar Recents/Starred row. Native bulk
    // actions own their own refetch + panel cleanup, so the in-flight guard
    // makes their self-emitted bridge events a no-op here.
    eventBus.on(CONTENT_STARRED, ({ itemType, itemId, starred } = {}) => {
      if (itemType !== 'practice' || !this.isActive || this._actionInFlight) return;
      const starredAt = starred ? new Date().toISOString() : null;

      // Patch the open detail + flip the visible panel header FIRST, before the
      // list's idempotency early-return: `currentPractice` is usually the same
      // object reference as its `allPractices` entry, so a list-first order
      // would bail out before the header ever updated.
      if (String(this.currentPractice?.id) === String(itemId)) {
        this.currentPractice.starredAt = starredAt;
        if (this._panelOpen) eventBus.emit('right-panel:set-star', { starred });
      }

      const practice = this.allPractices.find((item) => String(item?.id) === String(itemId));
      if (!practice || Boolean(practice.starredAt) === starred) return;
      practice.starredAt = starredAt;
      // Star is not a filter axis here (status/sort only) — no refilter needed.
      this.updateContentOnly();
    });

    eventBus.on(CONTENT_ARCHIVED, ({ itemType, itemId, archived } = {}) => {
      if (itemType !== 'practice' || !this.isActive || this._actionInFlight) return;
      const practice = this.allPractices.find((item) => String(item?.id) === String(itemId));
      if (!practice) return;

      const nextStatus = archived ? 'archived' : 'active';
      if (practice.status === nextStatus) return;
      practice.status = nextStatus;
      if (this.currentPractice === practice && this._panelOpen) {
        eventBus.emit('right-panel:close');
      }
      this.updateContentOnly();
    });

    eventBus.on(CONTENT_DELETED, ({ itemType, itemId } = {}) => {
      if (itemType !== 'practice' || !this.isActive || this._actionInFlight) return;
      const before = this.allPractices.length;
      this.allPractices = this.allPractices.filter(
        (item) => String(item?.id) !== String(itemId)
      );
      if (this.allPractices.length === before) return;
      if (String(this.currentPractice?.id) === String(itemId) && this._panelOpen) {
        eventBus.emit('right-panel:close');
      }
      this.updateContentOnly();
    });
  }

  /**
   * Forget which practice is open. Does NOT touch `_panelOpen` or the URL —
   * callers own those. Shared by the panel-closed handler,
   * `clearActiveDetailSelection` and `deactivate()`, so a card highlight can
   * never outlive its panel across a page change.
   * @private
   */
  _clearDetailSelection() {
    this.currentPractice = null;
    this._detailWrapper = null;
  }

  /**
   * Clear the routed practice detail selection while leaving the panel open.
   * Used when a non-routed page-info panel replaces the practice detail.
   */
  clearActiveDetailSelection() {
    this._clearDetailSelection();
    this.updateContentOnly();

    const url = new URL(window.location.href);
    let dirty = false;
    if (url.searchParams.has('practice')) { url.searchParams.delete('practice'); dirty = true; }
    if (url.searchParams.has('journey')) { url.searchParams.delete('journey'); dirty = true; }
    if (dirty) page.replace(url.pathname + (url.search ? url.search : ''));
  }

  async activate() {
    if (this.isActive) return;
    this.isActive = true;
    this.render();
    this._mountToolbarAction();
    await this.loadData();
    if (!this.isActive) return;
    this.render();
  }

  deactivate() {
    this.isActive = false;
    this._unmountToolbarAction();
    this.container.removeEventListener('click', this.handleActionClick);
    this.container.removeEventListener('contextmenu', this.handleRowContextMenu);
    this._rowMenu.close();
    this._resetSelectMode();
    this.stickyStack?.destroy();
    this.stickyStack = null;
    if (this.filterBar) {
      this.filterBar.destroy();
      this.filterBar = null;
    }
    if (this._panelOpen) {
      eventBus.emit('right-panel:close');
      this._panelOpen = false;
    }
    // The cross-page close's `right-panel:closed` lands after `isActive` is
    // already false, so the closed handler (rightly) ignores it and never
    // clears the selection. Clear it here, or the next `render()` on return
    // paints the old card active with no panel behind it.
    this._clearDetailSelection();
  }

  async loadData() {
    this.isLoading = true;
    this.loadError = null;

    try {
      // Every non-draft status in one fetch — the segments and the Show filter
      // are partitions of this list, so switching either never refetches.
      const all = await API.getPractices({ status: 'all', limit: 200 });
      this.allPractices = Array.isArray(all) ? all : [];
    } catch (error) {
      console.error('[PracticesSection] Load error:', error);
      this.loadError = error.message || 'Failed to load experiments';
    } finally {
      this.isLoading = false;
    }
  }

  // ===== PARTITIONING / SORTING =====

  /**
   * Sort one partition by its own time axis, honouring the sort control.
   * @param {Array<Object>} rows
   * @param {(practice: Object) => (string|Date|null)} keyFn - Row's time key
   * @returns {Array<Object>} New sorted array
   */
  _sortByTime(rows, keyFn) {
    return rows.slice().sort((a, b) => {
      const da = new Date(keyFn(a) || a.createdAt).getTime();
      const db = new Date(keyFn(b) || b.createdAt).getTime();
      return this.sortOrder === 'newest' ? db - da : da - db;
    });
  }

  /**
   * Open experiments — the Things to try segment's primary list. Overdue rows
   * float to the top regardless of the sort toggle — they're the ones that
   * need a report back — with the chosen sort order still governing the
   * order within each of the two groups.
   */
  _openPractices() {
    const sorted = this._sortByTime(
      this.allPractices.filter((p) => p.status === 'active'),
      (p) => p.createdAt
    );
    const overdue = [];
    const onTrack = [];
    for (const p of sorted) {
      const days = this._daysUntilWindowClose(p);
      (days !== null && days < 0 ? overdue : onTrack).push(p);
    }
    return [...overdue, ...onTrack];
  }

  /**
   * Archived experiments — `status: 'archived'`, which only a deliberate user
   * action produces. Reached through the Show filter inside Things to try.
   * Ordered by start date like the open queue: the Show filter changes which
   * rows you see, never how they sort.
   */
  _setAsidePractices() {
    return this._sortByTime(
      this.allPractices.filter((p) => p.status === 'archived'),
      (p) => p.createdAt
    );
  }

  /** Closed-with-a-finding experiments — the Findings segment. */
  _findings() {
    return this._sortByTime(
      this.allPractices.filter((p) => p.status === 'completed'),
      (p) => p.completedAt
    );
  }

  /**
   * The rows the page is showing right now — what the bar counts and the empty
   * state speaks for. Inside Things to try that is whichever side of the Show
   * filter is selected; Findings carries no filter of its own, so it is every
   * closed row.
   */
  getVisiblePractices() {
    if (this.view === 'findings') return this._findings();
    return this.listFilter === 'archived' ? this._setAsidePractices() : this._openPractices();
  }

  // ===== RENDERING =====

  render() {
    this.container.innerHTML = `
      <div class="explore-section">
        <div class="explore-container explore-container--fixed-action">
          <div class="explore-view--list" data-sticky-stack>
            ${renderPageInfoTitle('Experiments')}

            <div class="page-header content-width">
              <div class="page-header__subtitle-row">
                <p class="page-header__subtitle">Small tries. Real data.</p>
              </div>
            </div>

            ${this._renderNavBar()}

            <div class="content-width" data-filter-bar-mount="practices" data-sticky-slice></div>

            <div class="practice-content content-width">
              ${this.renderListContent()}
            </div>
          </div>
          ${this.renderFixedAction()}
        </div>
      </div>
    `;

    this._mountFilterBar();
    this.attachEventListeners();
    this._initStickyStack();
  }

  /**
   * The fixed bottom "New experiment" action — mobile-only (<1020px; desktop
   * shows the toolbar action instead, see _mountToolbarAction).
   * @returns {string} HTML string
   */
  renderFixedAction() {
    return `
      <div class="page-fixed-action" data-fixed-page-action>
        <div class="page-fixed-action__inner">
          <button class="btn btn--primary page-fixed-action__button" data-action="add-practice" type="button">New experiment</button>
        </div>
      </div>
    `;
  }

  /**
   * Render the Things to try / Findings page-toggle bar under the page title.
   * Its markup matches /discovery, /library and /partner; the only local part
   * is which two segments it holds.
   *
   * These are SEGMENTS, not filters: the card anatomy, the toolbar and the
   * empty state all change between them.
   * @returns {string} HTML string
   */
  _renderNavBar() {
    const items = this._renderNavItems();
    return renderSegmentNavBar(items, {
      ariaLabel: 'Experiments view',
      markerAttribute: 'data-practice-nav'
    });
  }

  /** The two segment buttons — no counts (Doug 2026-08-27). */
  _renderNavItems() {
    return `
      ${this.renderNavItem('open', 'Things to try')}
      ${this.renderNavItem('findings', 'Findings')}
    `;
  }

  /**
   * Repaint the segment buttons in place (active state). Clicks are
   * delegated (`handleActionClick`), so replacing the buttons costs no rebind.
   */
  _syncNavBar() {
    const nav = this.container.querySelector('[data-practice-nav]');
    if (nav) nav.innerHTML = this._renderNavItems();
  }

  /**
   * Render one segment button.
   * @param {'open'|'findings'} id - View identifier
   * @param {string} label - Display label
   * @param {number|null} [count] - Trailing count (null omits it)
   * @returns {string} HTML string
   */
  renderNavItem(id, label, count = null) {
    return renderSegmentNavItem({
      id,
      label,
      count,
      active: this.view === id,
      action: 'switch-view',
      dataAttribute: 'view'
    });
  }

  /**
   * Mount the desktop "New experiment" primary action into the page toolbar's
   * right slot (flush left of the X close button; hidden <1020px by CSS).
   * The slot lives outside `container`, so it survives list re-renders and is
   * mounted once per activation; PageManager.clearToolbarActions() also wipes
   * it on every page change.
   */
  _mountToolbarAction() {
    const slot = document.getElementById('page-toolbar-header-right-actions');
    if (!slot) return;
    slot.innerHTML = '';
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'btn btn--primary btn--small btn--toolbar';
    btn.textContent = 'New experiment';
    btn.addEventListener('click', () => this.handleAddPractice());
    slot.appendChild(btn);
  }

  /** Remove the toolbar action mounted by _mountToolbarAction(). Idempotent. */
  _unmountToolbarAction() {
    const slot = document.getElementById('page-toolbar-header-right-actions');
    if (slot) slot.innerHTML = '';
  }

  _mountFilterBar() {
    const mount = this.container.querySelector('[data-filter-bar-mount="practices"]');
    if (!mount) return;
    if (this.filterBar) {
      this.filterBar.destroy();
      this.filterBar = null;
    }
    if (this.selectMode) {
      this._renderSelectToolbar(mount);
      return;
    }
    // The segments own the open/closed axis; each view's bar carries whatever
    // axis is left. Things to try: the Show filter (running vs archived, URL-
    // routed). Findings: NONE — the rows no longer state a verdict, so a
    // Result lens would filter on a signal the list doesn't show, and with
    // predictions becoming optional it would hide rows that never had one.
    // The bar keeps its count noun, Sort and Select (`_hasMenu` stays true via
    // `sortConfig`, and an empty `filters` array maps to no filter group).
    const filters = this.view === 'findings' ? [] : [{
      id: 'status',
      label: 'Show',
      defaultValue: this.listFilter,
      // neutralValue defaults to options[0] ('active'), so the Archived view
      // reads as an applied filter — tinting the trigger and offering a reset.
      options: [
        {
          value: 'active',
          label: 'In progress',
          itemLabel: { singular: 'In progress', plural: 'In progress' }
        },
        {
          value: 'archived',
          label: 'Archived',
          itemLabel: { singular: 'Archived', plural: 'Archived' }
        }
      ]
    }];

    this.filterBar = new ResultsFilterBar({
      container: mount,
      itemLabel: this.view === 'findings'
        ? { singular: 'Finding', plural: 'Findings' }
        : { singular: 'In progress', plural: 'In progress' },
      count: this.getVisiblePractices().length,
      filters,
      sort: {
        defaultValue: this.sortOrder,
        options: [
          { value: 'newest', label: 'Newest' },
          { value: 'oldest', label: 'Oldest' }
        ]
      },
      actions: [
        { id: 'select', label: 'Select', icon: 'select', onClick: () => this._enterSelectMode() },
        {
          id: 'add-practice',
          label: 'New experiment',
          shortLabel: 'Experiment',
          variant: 'primary',
          onClick: () => this.handleAddPractice()
        }
      ],
      onChange: (state) => this._handleFilterChange(state)
    });
  }

  /**
   * Render the select-mode bulk toolbar: "N selected" + Star/Unstar, Archive
   * or Restore, Delete, Cancel. Disabled at zero selection or while a batch runs.
   *
   * Direction is derived from the SELECTION, not from a view — the retired
   * "archived" segment is gone and a selection can survive a filter flip. An
   * all-archived selection restores; anything containing an open row archives;
   * a selection of only findings has neither action available.
   * @param {HTMLElement} [mount] - Bar mount (looked up when omitted)
   */
  _renderSelectToolbar(mount = null) {
    const el = mount || this.container.querySelector('[data-filter-bar-mount="practices"]');
    if (!el) return;
    const n = this.selectedIds.size;
    const disabled = n === 0 || this._actionInFlight;
    const selected = this._selectedPractices();
    const allStarred = n > 0 && selected.every((p) => p.starredAt);
    const restorable = n > 0 && selected.every((p) => p.status === 'archived');
    const archivable = selected.some((p) => p.status === 'active');
    const archiveAction = restorable
      ? { action: 'select-restore', label: 'Restore', icon: 'unarchive', disabled }
      : { action: 'select-archive', label: 'Archive', icon: 'archive', disabled: disabled || !archivable };
    mountSelectToolbar(el, {
      count: n,
      actions: [
        { action: 'select-star', label: allStarred ? 'Unstar' : 'Star', icon: allStarred ? 'star' : 'star-outline', disabled },
        archiveAction,
        { action: 'select-delete', label: 'Delete', icon: 'trash', disabled },
        { action: 'select-cancel', label: 'Cancel', icon: 'close' }
      ]
    });
  }

  /**
   * Sort reorders in place; the Show filter changes membership, so it routes
   * through the URL (the single source of truth for which rows are on screen).
   *
   * Findings carries no filter, so its only bar change is Sort — and it must
   * return before the status branch below. Falling through would read a
   * `status` the Findings bar never rendered, resolve it to `active`, and
   * route a Findings sort click off to `?status=active`.
   * @param {{filters: {status?: string}, sort: string}} state - Bar state
   */
  _handleFilterChange(state) {
    this.sortOrder = state.sort;
    if (this.view === 'findings') { this.updateContentOnly(); return; }
    const nextFilter = state.filters?.status === 'archived' ? 'archived' : 'active';
    if (nextFilter !== this.listFilter) {
      this._routeToStatus(nextFilter === 'archived' ? 'archived' : 'active');
      return;
    }
    this.updateContentOnly();
  }

  /**
   * Switch segment (or flip the Show filter) through the URL, so both are
   * bookmarkable and the router stays the single source of truth.
   * `applyURLParams` maps the written `status` back onto view + filter state.
   * @param {'active'|'completed'|'archived'} nextStatus
   */
  _routeToStatus(nextStatus) {
    if (nextStatus === this._currentStatusParam()) return;
    const url = new URL(window.location.href);
    url.searchParams.set('status', nextStatus);
    url.searchParams.delete('practice');
    url.searchParams.delete('journey');
    page.replace(url.pathname + url.search);
  }

  /** The `?status=` value matching current view + filter state. */
  _currentStatusParam() {
    if (this.view === 'findings') return 'completed';
    return this.listFilter === 'archived' ? 'archived' : 'active';
  }

  renderListContent() {
    if (this.isLoading) return this.renderLoading();
    if (this.loadError) return this.renderError();
    return this.renderCards();
  }

  renderLoading() {
    return `<div class="practice-loading"><p>Loading experiments...</p></div>`;
  }

  renderError() {
    return `
      <div class="practice-error">
        <p>Failed to load experiments</p>
        <button class="btn btn--secondary" data-action="retry">Try Again</button>
      </div>
    `;
  }

  renderCards() {
    return this.view === 'findings' ? this._renderFindingsView() : this._renderOpenView();
  }

  /**
   * Things to try — one card per row on whichever side of the Show filter is
   * selected. The default view is the active queue ONLY; archived rows are a
   * filtered view of their own, never a shelf hanging off the bottom of it.
   * @returns {string} HTML string
   */
  _renderOpenView() {
    const archived = this.listFilter === 'archived';
    const items = archived ? this._setAsidePractices() : this._openPractices();
    if (!items.length) return renderEmptyState(archived ? EMPTY_COPY.archived : EMPTY_COPY.open);

    const activeId = this.currentPractice?.id ?? null;
    const selectCls = this.selectMode ? ' content-rows--select' : '';
    const variant = archived ? 'set-aside' : 'open';
    return `<div class="session-cards-container${selectCls}">${
      items.map((practice) => this.renderPracticeCard(practice, activeId, variant)).join('')
    }</div>`;
  }

  /**
   * Findings: every closed experiment, led by the name its close gave the
   * finding. No filter and no legend — the segment is the whole partition.
   * @returns {string} HTML string
   */
  _renderFindingsView() {
    const items = this._findings();
    if (!items.length) return renderEmptyState(EMPTY_COPY.findings);

    const activeId = this.currentPractice?.id ?? null;
    const selectCls = this.selectMode ? ' content-rows--select' : '';
    const cards = items.map((practice) => this.renderPracticeCard(practice, activeId, 'finding')).join('');
    return `<div class="session-cards-container${selectCls}">${cards}</div>`;
  }

  /**
   * Render one experiment row on the shared session-card pattern. Three
   * anatomies, one function — the leading slot, right rail, kebab and
   * select-mode behaviour are identical across all three:
   *
   *   `open`      title → thread meta.
   *   `set-aside` (the Archived rows) title → date meta. Quiet: no count pill.
   *   `finding`   the finding's own name → its description, one clamped line
   *               (`_renderFindingBody`). A legacy close that never authored
   *               a name falls back to the experiment title over the old
   *               lesson/learned headline. No verdict word and no provenance
   *               line on either path.
   *
   * The leading slot carries the same practices icon on EVERY variant,
   * unread marker included — a finding you have not opened is the row on the
   * page you most want to spot. (An intermediate design put a verdict dot
   * here instead and dropped the unread marker with it; the verdict is off
   * the row entirely now, so the slot stays the icon.)
   *
   * The right rail carries the status chip (`getPracticeStatusInfo`) on
   * open/archived rows and the bare close date on findings
   * (`_renderFindingDate`); `.note-row__status`'s hover-swap hides it in
   * favour of the kebab + chevron. In select mode the checkbox rides the
   * leading slot so the row shifts right as a whole; a starred experiment
   * shows a star marker.
   *
   * Outside select mode the row is a disclosure control for the right panel
   * (click opens, re-click closes — see `handleListClick`), so it carries
   * `aria-expanded`; in select mode it is a toggle and carries `aria-pressed`.
   *
   * @param {Object} practice - Serialized practice row
   * @param {string|null} [activeId] - Practice currently open in the panel
   * @param {'open'|'set-aside'|'finding'} [variant] - Card anatomy
   * @returns {string} HTML string
   */
  renderPracticeCard(practice, activeId = null, variant = 'open') {
    const { id, title, definition } = practice;
    const selected = this.selectMode && this.selectedIds.has(id);
    const isOpenInPanel = Boolean(activeId) && id === activeId;
    const activeCls = isOpenInPanel || selected ? ' session-card--active' : '';

    // A named finding leads with the FINDING's name, not the experiment's —
    // that is the name the user met at close and the one they will look for
    // here. Everything else (open, archived, legacy findings) leads with the
    // experiment title.
    const experimentTitle = (typeof title === 'string' && title.trim())
      || definition?.name
      || 'Experiment';
    const cardTitle = (variant === 'finding' && this._findingTitle(practice)) || experimentTitle;

    const unread = !practice.viewedAt;
    const slot = this.selectMode
      ? `<span class="session-card__slot"><input type="checkbox" class="note-row__checkbox" tabindex="-1" aria-hidden="true"${selected ? ' checked' : ''}></span>`
      : `<span class="session-card__slot">${practiceIconHtml(unread, practice)}</span>`;
    const star = practice.starredAt
      ? `<span class="note-row__star" aria-label="Starred">${STAR_SVG}</span>`
      : '';
    // A colored dot+chip (.notification-label) is reserved for open/archived
    // states that need attention (Overdue, Archived); the default on-track
    // date is plain gray text — the same treatment other list rows give a
    // date. A finding closed the loop already, so its rail is a plain date.
    let statusCls;
    let statusInner;
    if (variant === 'finding') {
      statusCls = 'date';
      statusInner = this._renderFindingDate(practice);
    } else {
      const { text: statusText, cls, indicatorColor } = this.getPracticeStatusInfo(practice);
      statusCls = cls;
      statusInner = indicatorColor
        ? `<span class="notification-label notification-label--${indicatorColor}">${statusText}</span>`
        : `<span class="note-row__date">${this.escapeHtml(statusText)}</span>`;
    }
    const kebab = this.selectMode ? '' : this.renderRowOptions(practice);

    const body = variant === 'finding'
      ? this._renderFindingBody(practice, cardTitle, star)
      : this._renderOpenBody(practice, cardTitle, star, variant);

    return `
      <div class="card session-card practice-card practice-card--${variant} note-row session-card--iconed${activeCls}"
           data-practice-id="${this.escapeHtml(id)}"
           role="button"
           tabindex="0"
           ${this.selectMode ? `aria-pressed="${selected}"` : `aria-expanded="${isOpenInPanel}"`}>
        ${slot}
        <div class="session-card__content">${body}</div>
        <span class="note-row__meta">
          <span class="note-row__status practice-row__status practice-row__status--${statusCls}">${statusInner}</span>
          ${kebab}
          <span class="session-card__chevron note-row__chevron" aria-hidden="true">
            <svg width="19" height="19" viewBox="0 0 16 16">
              <path d="M6 4L10 8L6 12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" fill="none"/>
            </svg>
          </span>
        </span>
      </div>
    `;
  }

  /**
   * Title-led card body (open + archived rows) — a single title row (Doug
   * 2026-08-27: the "Testing:" prediction line came off the card; the
   * prediction lives on the detail page).
   * @returns {string} HTML string
   */
  _renderOpenBody(practice, cardTitle, star, variant) {
    const logCount = (practice.logs || []).length;
    const countPill = variant === 'open' && logCount > 0
      ? `<span class="note-row__count" aria-label="${logCount} report${logCount !== 1 ? 's' : ''}">${logCount}</span>`
      : '';

    // Same meta line whichever side of the Show filter this row is on: a
    // thread link when there is one, otherwise nothing. Archived rows used to
    // print a bare date here, which only duplicated the detail page's
    // "Started ·" pill and gave the row a second line to say it.
    const meta = this._renderThreadReplyLine(practice);

    return `
      <div class="session-card__title-row">
        ${star}
        <h4 class="session-card__title session-card__title--light">${this.escapeHtml(cardTitle)}</h4>${countPill}
      </div>
      ${meta}
    `;
  }

  /**
   * Card body for findings — TWO lines, both about the finding itself.
   *
   *   line 1  star? + the finding's own name (`cardTitle`, resolved by
   *           `renderPracticeCard`)
   *   line 2  its description, clamped to one line
   *
   * Legacy rows — closes from before the finding was named, and partial
   * emissions that fail the D1a pair test — keep the complete old anatomy:
   * the experiment title on line 1 over the lesson-else-learned headline on
   * line 2. No surface branches on half the pair (`_findingNamed`).
   *
   * Both lines take the /search two-row card's grammar — title at the base
   * `.session-card__title` weight (NOT `--light`, which the open/archived rows
   * use), subtitle in the `.session-card__summary` register. A finding row and
   * a /search result are the same shape doing the same job, so they read the
   * same. There is no report-count pill: the count is history on a closed
   * experiment, and the detail page's Activity feed carries it.
   *
   * WHY THE FINDING LEADS. The 2026-09-01 ruling flipped this row back to the
   * experiment title on the grounds that the nav card is a locator rather than
   * a content surface, and that the learning had no stable short name to be
   * located by — every candidate was a borrowed field (`lesson`, a quoted
   * `learned`) that the user had never met as a name. Naming the finding at
   * close changes that premise: the title is written in the room, in the
   * user's register, and is the FIRST thing they read on the close card. They
   * come here looking for a finding by the name it already has, so the locator
   * argument now points at the finding title, not away from it. Re-flipping
   * this again means re-deciding that premise, not restating either ruling.
   *
   * NO VERDICT ON THE ROW. The description says in words whether the belief
   * held, so a label beside it is a second, coarser copy of the same fact;
   * with predictions becoming optional it would also be present on only some
   * rows, which reads as inconsistency rather than as absence. A bare dot
   * would need a legend to decode, and dropping the legend is what let this
   * row simplify in the first place. The verdict survives on the detail
   * page's plate, where it has a full sentence and room for its colour.
   *
   * NO PROVENANCE LINE. The experiment title is one click away, inside the
   * detail page's "The experiment" zone; a third line would push a row tuned
   * for two lines to three, and printing a non-clickable title here would
   * borrow the "Evolved from" link register for a dead affordance.
   * @returns {string} HTML string
   */
  _renderFindingBody(practice, cardTitle, star) {
    const description = this._findingDescription(practice);
    // Legacy fallback only — on a named finding the description IS line 2, and
    // the borrowed headline never renders.
    const headline = description ? null : this._findingHeadline(practice);
    const lineText = description || headline?.text || '';

    // UNQUOTED on both paths. `description` is synthesis and is never quoted
    // anywhere (the detail lede included); the legacy `learned` headline drops
    // its quotes HERE because the line is clamped to one line, and a truncated
    // quotation ends on a dangling open quote — the closing mark is exactly
    // what the ellipsis eats.
    const second = lineText
      ? `<span class="practice-card__finding-line">${this.escapeHtml(lineText)}</span>`
      : '';

    return `
      <div class="session-card__title-row">
        ${star}
        <h4 class="session-card__title">${this.escapeHtml(cardTitle)}</h4>
      </div>
      ${second}
    `;
  }

  /**
   * The finding row's right rail — the bare close date ("Aug 22"), in the same
   * quiet gray as every other list row's date. No "closed" prefix: every row
   * in this segment is closed, so the word states the segment rather than the
   * row, and the rail is the one column where the eye scans a column of dates.
   *
   * `completedAt` is nullable on older rows; the detail variant falls back to
   * `createdAt` and so does this. If neither resolves to a real date the rail
   * is EMPTY. The reserved column stays either way, so the hover swap to
   * kebab + chevron never shifts the row.
   * @param {Object} practice - Completed practice row
   * @returns {string} HTML string ('' when the row carries no usable date)
   */
  _renderFindingDate(practice) {
    const closedAt = practice.completedAt || practice.createdAt;
    const closed = closedAt ? new Date(closedAt) : null;
    if (!closed || Number.isNaN(closed.getTime())) return '';
    return `<span class="practice-card__closed-date">${this.escapeHtml(this.formatDate(closedAt))}</span>`;
  }

  /**
   * "Evolved from #N" as a notes-style reply line (`.note-row__reply-line`,
   * shared with /notes' reply-pair sublink — components/content-row.css): a
   * muted arrow + label, then the parent's own title as a clickable link.
   * Clicking opens the parent's detail panel in place (`_openParentPractice`),
   * mirroring how a note's "In reply to" link jumps to its parent.
   * @param {Object} practice
   * @returns {string} HTML string ('' for a standalone experiment)
   */
  _renderThreadReplyLine(practice) {
    const parent = practice.parent;
    if (!parent || typeof parent.practiceNumber !== 'number') return '';
    const parentTitle = typeof parent.title === 'string' && parent.title.trim()
      ? parent.title.trim()
      : `Experiment #${parent.practiceNumber}`;
    return `
      <div class="note-row__reply-line">
        <span class="note-row__reply-arrow" aria-hidden="true">↳</span>
        <span class="note-row__reply-label">Evolved from</span>
        <a class="note-row__reply-link" data-action="open-parent-practice" data-parent-number="${this.escapeHtml(String(parent.practiceNumber))}" role="button" tabindex="0">${this.escapeHtml(parentTitle)}</a>
      </div>
    `;
  }

  /**
   * The right-rail status for one experiment — the shared vocabulary in
   * `components/practiceDrillHelper.js`, so this page and every surface that
   * lists an experiment away from it (session detail) word a row's state
   * identically. Kept as an instance method because the row renderers and the
   * overdue sort all call it as one.
   *
   * @param {Object} practice - Practice row
   * @returns {{text: string, cls: string, indicatorColor: string|null}}
   */
  getPracticeStatusInfo(practice) {
    return practiceStatusInfo(practice);
  }

  /**
   * Render the hover-revealed kebab options for one experiment row: Report
   * back (open only, mirroring the detail panel's primary button),
   * Star/Unstar, Archive (open) | Restore (archived) — omitted for findings
   * — and Delete (danger). Reuses the shared options-dropdown classes.
   * @param {Object} practice - Practice row
   * @returns {string} HTML string
   */
  renderRowOptions(practice) {
    const items = [];
    if (practice.status === 'active') {
      items.push({ action: 'row-discuss', label: 'Report back' });
    }
    items.push({ action: 'row-star', label: practice.starredAt ? 'Unstar' : 'Star', icon: practice.starredAt ? 'star' : 'star-outline' });
    if (practice.status === 'active') {
      items.push({ action: 'row-archive', label: 'Archive' });
    } else if (practice.status === 'archived') {
      items.push({ action: 'row-restore', label: 'Restore' });
    }
    items.push({ action: 'row-delete', label: 'Delete', variant: 'danger' });
    return renderRowKebab({
      items,
      dataAttrs: { 'data-practice-id': practice.id },
      ariaLabel: 'Experiment options',
    });
  }

  // ===== EXPERIMENT-FIELD READERS =====

  /** Newest PracticeLog on a practice, or null. */
  _latestLog(practice) {
    return (practice.logs || [])
      .filter(Boolean)
      .slice()
      .sort((a, b) => new Date(b.loggedAt).getTime() - new Date(a.loggedAt).getTime())[0] || null;
  }

  /**
   * The verdict from the newest report that recorded one. Soft slot: unknown
   * values read as absent rather than rendering a bogus chip.
   * @returns {string|null}
   */
  _latestVerdict(practice) {
    const log = (practice.logs || [])
      .filter((entry) => entry && VERDICT_CHIPS[entry.predictionVerdict])
      .sort((a, b) => new Date(b.loggedAt).getTime() - new Date(a.loggedAt).getTime())[0];
    return log ? log.predictionVerdict : null;
  }

  /**
   * Whole-day delta from today to the window close (0 = closes today, negative
   * = already passed). Null when the row carries no window — legacy rows the
   * reconciliation sweep hasn't reached yet, and any unparseable value. Shared
   * with every surface that reads a row's status (`practiceDrillHelper`).
   * @returns {number|null}
   */
  _daysUntilWindowClose(practice) {
    return daysUntilWindowClose(practice);
  }

  /**
   * The ONE report every takeaway field is read from: the newest log that
   * ITSELF passes both gates below. Single-log semantics are load-bearing —
   * pick the log, then read fields within it, never a per-field search
   * across logs (that would let a declined close's `lesson` leak onto a
   * Finding closed later without one) — and never gate on one log while
   * rendering another (that would authorize on an older confirmed log while
   * still surfacing the newest declined one).
   *
   * Two clauses, both required, mirroring the server-side gate
   * (`findingReader`, `backend/src/services/practices/findingReader.js`):
   *   1. `takeawayJson` is a non-null object — a card exists to read at all.
   *   2. `outcome` is a CONFIRMED value (`CONFIRMED_TAKEAWAY_OUTCOMES`) — the
   *      declined-close trap: `recordCompletionDecline` never touches the
   *      practice row, so the post-processor can still write a log with a
   *      fully populated `takeawayJson` and `outcome: null`. Without this
   *      clause, a completed practice whose *newest* takeaway log is an
   *      earlier declined card would have that declined text read as an
   *      established finding by every surface below.
   *
   * @param {Object} practice
   * @returns {Object|null} PracticeLog row, or null
   */
  _findingTakeawayLog(practice) {
    return (practice.logs || [])
      .filter((log) => log
        && log.takeawayJson && typeof log.takeawayJson === 'object'
        && CONFIRMED_TAKEAWAY_OUTCOMES.includes(log.outcome))
      .sort((a, b) => new Date(b.loggedAt).getTime() - new Date(a.loggedAt).getTime())[0] || null;
  }

  /**
   * The finding's NAME and its one-or-two-sentence conclusion, as ONE unit —
   * the browser-side mirror of `readFinding`'s `title`/`description` pair
   * (`backend/src/services/practices/findingReader.js`), which the frontend
   * cannot import.
   *
   * Both or neither (D1a). A finding is *named* iff BOTH slots are non-blank
   * strings on the SAME selected log; a partial emission is ignored outright
   * rather than rendering half a new card. The rule lives in this one helper,
   * and `_findingTitle`/`_findingDescription` are thin readers over it, so a
   * later caller cannot reach one slot without the other and drift the
   * predicate — which is what would let the nav card render new and the detail
   * page legacy for the same row.
   *
   * Read-side rule only: the sanitizer stores whatever the model emitted.
   * Legacy rows carry neither key and land on the `{null, null}` branch, which
   * is what every legacy render path already keys on.
   *
   * @param {Object} practice
   * @returns {{title: string|null, description: string|null}}
   */
  _findingNamed(practice) {
    const takeaway = this._findingTakeawayLog(practice)?.takeawayJson;
    const title = typeof takeaway?.title === 'string' ? takeaway.title.trim() : '';
    const description = typeof takeaway?.description === 'string' ? takeaway.description.trim() : '';
    if (!title || !description) return { title: null, description: null };
    return { title, description };
  }

  /**
   * The finding's own name — null unless the close authored BOTH halves of the
   * pair (see `_findingNamed`). Never quoted: it is model synthesis, and the
   * quote register on these surfaces means the user's own words.
   * @param {Object} practice
   * @returns {string|null}
   */
  _findingTitle(practice) {
    return this._findingNamed(practice).title;
  }

  /**
   * The finding's one-or-two-sentence conclusion — null unless the close
   * authored BOTH halves of the pair (see `_findingNamed`). Never quoted, for
   * the same reason the title isn't.
   * @param {Object} practice
   * @returns {string|null}
   */
  _findingDescription(practice) {
    return this._findingNamed(practice).description;
  }

  /**
   * The finding's `lesson` — the model's one-sentence directive, when the
   * close earned one. Exists as its own reader (rather than folding into
   * `_findingConclusion`) because the two slots carry different attribution:
   * `learned` is the user's words and keeps its curly quotes; `lesson` is
   * synthesis and is NEVER quoted anywhere it renders.
   * @param {Object} practice
   * @returns {string|null}
   */
  _findingLesson(practice) {
    const lesson = this._findingTakeawayLog(practice)?.takeawayJson?.lesson;
    return typeof lesson === 'string' && lesson.trim() ? lesson.trim() : null;
  }

  /**
   * The `learned` slot of the finding's takeaway log, joined into one string —
   * the user's own words, and the only part of a takeaway that may be quoted.
   * Null when the slot is missing, empty, or carries nothing but blanks.
   * @param {Object} practice
   * @returns {string|null}
   */
  _findingLearned(practice) {
    const takeaway = this._findingTakeawayLog(practice)?.takeawayJson;
    const learned = (Array.isArray(takeaway?.learned) ? takeaway.learned : [])
      .filter((item) => typeof item === 'string' && item.trim());
    return learned.length ? learned.join(' ').trim() : null;
  }

  /**
   * The ONE reader for the frontier slot, across both eras of stored card —
   * the browser-side mirror of `readNextStep`
   * (`backend/src/services/practices/findingReader.js`), which the frontend
   * cannot import.
   *
   * Since 2026-09 `takeawayJson.next` holds at most one OBJECT
   * (`{title, description?, practiceCode?}`). Rows written before that hold an
   * array of plain strings and are read, never migrated — they are historical
   * records.
   *
   * ⚠️ A legacy row may hold MORE THAN ONE string. The original reader joined
   * arbitrarily many, so taking `next[0]` would silently drop half of an
   * existing user's finding. Join all of them first, then treat the joined
   * result as the title.
   *
   * BOTH readers on this page go through here — the card in
   * `_renderFindingResults` and the BUILD opener in `handleDetailClick`. A
   * second, independent parse is exactly what put `"[object Object]"` into a
   * live prompt: `.filter(Boolean).join(' ')` keeps an object and stringifies
   * it. Never re-derive the frontier anywhere else.
   *
   * One deliberate divergence from the backend: `practiceCode` is passed
   * through un-validated. The catalog (`isValidPracticeCode`) is backend-only,
   * and the sanitizer already dropped unrecognised codes on the write side;
   * nothing here consumes the field (D4).
   *
   * @param {Object} practice
   * @returns {{title: string, description: string|null, practiceCode: string|null}|null}
   */
  _findingNextStep(practice) {
    const next = this._findingTakeawayLog(practice)?.takeawayJson?.next;
    if (!Array.isArray(next) || next.length === 0) return null;

    const structured = next.find((item) => item && typeof item === 'object' && !Array.isArray(item));
    if (structured) {
      const title = typeof structured.title === 'string' ? structured.title.trim() : '';
      if (!title) return null;
      const description = typeof structured.description === 'string' && structured.description.trim()
        ? structured.description.trim()
        : null;
      const practiceCode = typeof structured.practiceCode === 'string' && structured.practiceCode.trim()
        ? structured.practiceCode.trim()
        : null;
      return { title, description, practiceCode };
    }

    const joined = next
      .filter((item) => typeof item === 'string' && item.trim())
      .map((item) => item.trim())
      .join(' ')
      .trim();
    return joined ? { title: joined, description: null, practiceCode: null } : null;
  }

  /**
   * LEGACY headline — what a finding is called when its close never authored a
   * name (`_findingNamed` returns the null pair). `lesson` (synthesis) wins and
   * is never quoted; otherwise the joined `learned` conclusion, quoted because
   * it is the user's words. Mirrors `readHeadline`'s legacy-only role in
   * `findingReader`. A NAMED finding never reaches here — its title and
   * description are the row and the lineage line.
   *
   * Null is the branch predicate for that legacy anatomy, and it is
   * deliberately narrower than "has a takeaway log": `_findingTakeawayLog`
   * accepts any object, so `{}` or an array selects as the takeaway while
   * carrying nothing to headline, and `_findingConclusion` would then fall
   * through to a report title or session summary — text that is not the
   * finding. Every legacy caller reads this one helper so the predicate and the
   * render can't drift apart.
   * @param {Object} practice
   * @returns {{text: string, quoted: boolean}|null}
   */
  _findingHeadline(practice) {
    const lesson = this._findingLesson(practice);
    if (lesson) return { text: lesson, quoted: false };
    const learned = this._findingLearned(practice);
    return learned ? { text: learned, quoted: true } : null;
  }

  /**
   * The finding's conclusion. Sources the newest report carrying a takeaway
   * artifact and reads its `learned` slot — the user's own words, so `quoted`.
   *
   * Null-display fallback for legacy rows (§6.2 matrix): missing or malformed
   * `takeawayJson` → the report's generated title → its session summary →
   * (caller) the experiment title. Fallback text is system-generated, so it
   * carries `quoted: false` — the quote register means the user's words, and
   * a report title rendered inside curly quotes would claim an attribution it
   * doesn't have. Never an empty quote.
   * @param {Object} practice
   * @returns {{text: string, quoted: boolean}|null}
   */
  _findingConclusion(practice) {
    const learned = this._findingLearned(practice);
    if (learned) return { text: learned, quoted: true };

    const fallbackSource = this._findingTakeawayLog(practice) || this._latestLog(practice);
    const title = typeof fallbackSource?.title === 'string' ? fallbackSource.title.trim() : '';
    if (title) return { text: title, quoted: false };
    const summary = typeof fallbackSource?.sessionSummary === 'string'
      ? fallbackSource.sessionSummary.trim()
      : '';
    return summary ? { text: summary, quoted: false } : null;
  }

  // ===== DETAIL VIEW =====

  /**
   * Render the right-panel content for one experiment.
   *
   * Chrome contract (detail-page style guide §6 — CTA = panel-header pill
   * only): the star toggle and the options dropdown ride the panel header, and
   * exactly one dark pill sits beside them — "Report back" while the window is
   * open, "Restore" once the experiment is archived, nothing on a Finding. The
   * promoted action is never duplicated in the kebab, and the page body carries
   * no button of its own — except the Finding frontier's quiet
   * "Build a new experiment" pill (style guide §4's one sanctioned
   * exception; see `_renderFindingResults`).
   */
  showPracticeDetail(practiceId) {
    const practice = this.allPractices.find((p) => p.id === practiceId);
    if (!practice) {
      console.error('[PracticesSection] Practice not found:', practiceId);
      return;
    }

    this.currentPractice = practice;
    this._panelOpen = true;
    this.updateContentOnly();
    eventBus.emit('right-panel:open', this.buildPracticeDetailPayload(practice));
    this.markPracticeViewed(practice);
  }

  /**
   * Build the right-panel payload for one experiment. Split out of
   * `showPracticeDetail` so a foreign surface can drill this exact panel
   * (`buildDrilledPracticePayload`) instead of reimplementing 750 lines of
   * detail rendering.
   *
   * NOT pure, and can't be: it writes `this._detailWrapper`, which
   * `attachDetailEventListeners` reads. Callers must also have set
   * `this.currentPractice` and have the row present in `this.allPractices` —
   * `handleDetailClick`'s in-body branches resolve through both.
   *
   * @param {Object} practice - The practice to render
   * @param {Object} [opts]
   * @param {boolean} [opts.portable=false] - Wire actions that work while this
   *   section is NOT the active page (see `_portableDetailActions`)
   * @returns {Object} payload for `right-panel:open` / `right-panel:drill`
   */
  buildPracticeDetailPayload(practice, { portable = false } = {}) {
    const status = practice.status;

    const wrapper = document.createElement('div');
    wrapper.appendChild(this.renderDetail(practice));
    this._detailWrapper = wrapper;
    this.attachDetailEventListeners();

    const actions = portable
      ? this._portableDetailActions(practice)
      : {
          onStar: async (next) => {
            const result = await API.starPractices([practice.id], next);
            const ok = result?.count === 1;
            if (ok) {
              practice.starredAt = next ? new Date().toISOString() : null;
              if (this.isActive) this.updateContentOnly();
              // Confirmed-success only — updates the sidebar's Starred section live.
              emitContentStarred('practice', practice.id, next);
            }
            return ok;
          },
          onOption: (action) => this._handleDetailOption(action, practice.id),
          onReportBack: () => this.handleDiscuss(practice.id),
          onRestore: () => this._handleDetailOption('restore', practice.id)
        };

    // Options dropdown: Archive (active) + Delete. Restore is NOT repeated
    // here — on an archived experiment it is the header's own dark pill.
    const optionItems = [];
    if (status === 'active') optionItems.push({ action: 'archive', label: 'Archive' });
    optionItems.push({ action: 'delete', label: 'Delete', variant: 'danger' });

    const payload = {
      title: '',
      titleAlign: 'left',
      starButton: { starred: !!practice.starredAt, onClick: actions.onStar },
      optionsDropdown: { items: optionItems, onAction: actions.onOption },
      content: wrapper,
      contentType: 'practice-detail'
    };

    if (status === 'active') {
      payload.primaryButton = { label: 'Report back', onClick: actions.onReportBack };
    } else if (status === 'archived') {
      payload.primaryButton = { label: 'Restore', onClick: actions.onRestore };
    }

    return payload;
  }

  /**
   * Build this panel for a surface that DRILLED into it — a session note's
   * Experiments zone, say — where /experiments is not the rendered page.
   *
   * Two things make that safe. The row is seeded into `allPractices` and
   * `currentPractice` so the detail's own in-body handlers resolve it; and the
   * actions are the portable ones, because the page-owned path (`_runBulk`)
   * early-returns on `!this.isActive` — from a foreign page its Delete never
   * fires at all and its Archive lands server-side with every toast skipped.
   *
   * The body's three navigation affordances keep their page behaviour, which
   * from a drilled panel means: a lineage link (`view-practice`) navigates to
   * /experiments — a deliberate exit, not a deeper drill, so the back arrow
   * ends there; a "Session N" tag drills the session (or falls back to /search
   * at the depth cap); and the Finding frontier's build pill resolves the row
   * this method just seeded.
   *
   * @param {Object} practice - Serialized practice row (from the calling surface)
   * @returns {Object} payload for `right-panel:drill`
   */
  buildDrilledPracticePayload(practice) {
    const known = this.allPractices.find((p) => p.id === practice.id);
    if (known) {
      // Prefer the list's own row (it may be fresher), but take anything the
      // caller has that the list row lacks.
      Object.assign(known, { ...practice, ...known });
    } else {
      this.allPractices = [...this.allPractices, practice];
    }
    const row = known || practice;

    this.currentPractice = row;
    const payload = this.buildPracticeDetailPayload(row, { portable: true });
    this.markPracticeViewed(row);
    return payload;
  }

  /**
   * The star / kebab / primary-pill actions for a DRILLED panel: direct API
   * calls, each surfacing its own outcome and syncing other surfaces through
   * `contentSync`, then popping back to the page that drilled in rather than
   * closing the panel (`right-panel:close` drains the whole drill stack, taking
   * the session note behind it with it).
   *
   * Mirrors `SessionsSection`'s static `confirmAndDeleteSession` /
   * `toggleArchiveSession`, which exist for exactly this reason.
   *
   * @param {Object} practice - The practice shown in the panel
   * @returns {{onStar: Function, onOption: Function, onReportBack: Function, onRestore: Function}}
   */
  _portableDetailActions(practice) {
    const syncLocal = () => {
      if (this.isActive) this.updateContentOnly();
    };

    const archiveOrRestore = async (archived) => {
      const result = archived
        ? await API.archivePractice(practice.id)
        : await API.restorePractice(practice.id);
      if (result?.success === false || result === null) {
        notifyArchiveFailed({ count: 1, type: 'practice', archived });
        return;
      }
      practice.status = archived ? 'archived' : 'active';
      emitContentArchived('practice', practice.id, archived);
      notifyArchived({ count: 1, type: 'practice', archived });
      syncLocal();
      eventBus.emit('right-panel:pop');
    };

    return {
      onStar: async (next) => {
        const result = await API.starPractices([practice.id], next);
        const ok = result?.count === 1;
        if (ok) {
          practice.starredAt = next ? new Date().toISOString() : null;
          emitContentStarred('practice', practice.id, next);
          syncLocal();
        }
        return ok;
      },
      onOption: async (action) => {
        if (action === 'archive') return archiveOrRestore(true);
        if (action === 'restore') return archiveOrRestore(false);
        if (action !== 'delete') return;

        const confirmed = await modalService.confirm({
          title: 'Delete experiment',
          message: 'Permanently delete this experiment? This also deletes its reports.',
          confirmText: 'Delete',
          cancelText: 'Cancel',
          variant: 'danger'
        });
        if (!confirmed) return;

        const result = await API.deletePractice(practice.id);
        if (result?.success === false || result === null) {
          window.notificationManager?.showError("Couldn't delete that. Please try again.");
          return;
        }
        this.allPractices = this.allPractices.filter((p) => p.id !== practice.id);
        emitContentDeleted('practice', practice.id);
        syncLocal();
        eventBus.emit('right-panel:pop');
      },
      // Both hand off to chat, which leaves the panel behind on its own terms —
      // the page-owned handlers work unchanged here.
      onReportBack: () => this.handleDiscuss(practice.id),
      onRestore: () => archiveOrRestore(false)
    };
  }

  /**
   * Render the right-panel detail — ONE template with two variants.
   *
   * Both variants share one grammar: zone header → the lead on open ground
   * (`.practice-detail__lead`, the page's one emphasised line) → a
   * `.sd-callout-stack` of callouts, each carrying one of the three semantic
   * hues on its eyebrow (style guide §2: measure / point / claim).
   *
   * **Action-led** (open / kept open / archived): "What to do" → the action
   * as the lead → What To Watch For (measure) → What We're Testing (claim,
   * only with a prediction) → Why This Matters (point, collapsible; open on
   * the first visit, collapsed after) → Activity. A kept-open experiment gets
   * NO "why it's still open" section — the status pill and the latest
   * Activity entry already tell that story — and an archived one is the
   * identical page with its own status pill and a Restore header CTA.
   *
   * **Finding** (closed): "What we learned" → the finding's lede → What
   * happened (measure) → Carry forward (point) → What we tested (claim: the
   * prediction, closed by the verdict line) → the frontier zone → "The
   * experiment" disclosure → Activity.
   *
   * Both share the header grammar: sans `.sd-title`, one meta row of gray fact
   * pills + a filled session link that darkens on hover, and eyebrow+rule
   * zone headers.
   * No in-body CTA in either, except the Finding frontier link (style guide
   * §4 exception) — see `showPracticeDetail` and `_renderFindingResults`.
   */
  renderDetail(practice) {
    const container = document.createElement('div');
    container.className = 'practice-detail';
    container.innerHTML = practice.status === 'completed'
      ? this._renderFindingVariant(practice)
      : this._renderActionVariant(practice);
    // Zone toggles. Wired on the container rather than in showPracticeDetail
    // so every caller of renderDetail gets them — the panel host and tests
    // alike — and so the wiring cannot outlive the markup it belongs to.
    wireDisclosures(container);
    return container;
  }

  /**
   * The "Started · date" fact pill, or '' when the row has no start on record.
   * Both detail variants carry it, identically — a Finding's header says when
   * the experiment began, not the span it ran for: the completion pill beside
   * it already gives the other end, and two dates in one pill read as a
   * measurement of duration, which is not what either date means.
   * @param {Object} practice
   * @returns {string} HTML string
   */
  _startedPill(practice) {
    return this._factPill(
      practice.createdAt ? `Started · ${this.formatDate(practice.createdAt)}` : ''
    );
  }

  /**
   * Shared header: sans title + one meta row.
   *
   * `titleOverride` exists for exactly one caller — a NAMED finding, whose H1
   * is the finding's own name rather than the experiment's (the experiment
   * title moves down into "The experiment" zone). Optional, and blank-safe:
   * anything falsy falls through to the same title every other page shows, so
   * an unnamed close can never produce an empty H1.
   * @param {Object} practice
   * @param {Array<string>} pills - Meta atoms, already HTML
   * @param {string|null} [titleOverride] - H1 text in place of the experiment title
   * @returns {string} HTML string
   */
  _renderDetailHeader(practice, pills, titleOverride = null) {
    const headerTitle = titleOverride || practice.title || practice.definition?.name || 'Experiment';
    const session = practice.session;
    const atoms = pills.filter(Boolean);
    if (session?.sessionNumber) {
      // Header meta row, so the shared `.tag--meta-link` (components/tags.css):
      // shaped like the neighbouring fact pills and separated from them by its
      // blue fill alone. The Activity feed's links below keep `.tag--session`'s
      // outline — see that class's comment for why the row matters.
      atoms.push(`<span class="tag tag--meta-link" data-action="view-session" data-session-id="${this.escapeHtml(session.id || '')}" data-session-number="${this.escapeHtml(String(session.sessionNumber))}">Session ${this.escapeHtml(String(session.sessionNumber))} ${iconArrowUpRight()}</span>`);
    }
    return `
      <div class="practice-detail__header">
        <h1 class="sd-title">${this.escapeHtml(headerTitle)}</h1>
        <div class="practice-detail__meta sd-metadata">${atoms.join('')}</div>
      </div>
    `;
  }

  /** Gray fact pill for the detail meta row. */
  _factPill(text) {
    return text ? `<span class="sd-metadata__text">${this.escapeHtml(text)}</span>` : '';
  }

  /**
   * Action-led variant — active and archived experiments.
   * @returns {string} HTML string
   */
  _renderActionVariant(practice) {
    const { action, rationale } = practice;
    // First visit (not yet viewed) → the rationale box starts expanded;
    // subsequent visits → collapsed. Same unread signal as the NEW badge.
    const isFirstVisit = !practice.viewedAt;

    const header = this._renderDetailHeader(practice, [
      this._factPill(this.getPracticeStatusInfo(practice).text),
      this._startedPill(practice)
    ]);

    let body = '<div class="practice-detail__body practice-detail__standalone">';

    // The action is the lead, on open ground — the page has a single
    // emphasised element and it is the thing to go and do; the zone header
    // already marks the zone, so no plate around it. Everything else is a
    // callout stacked beneath, in the order you need them: what to
    // watch for (measure), then the claim under test (claim), then — quieter,
    // and collapsed once you have read it — why it matters (point). The lead
    // is not a `[data-takeaway-toggle]` element, so clicking it toggles
    // nothing.
    const notice = this._noticeBoxOptions(practice, false);
    const prediction = this._predictionBoxOptions(practice, false);
    const why = rationale
      ? { label: 'Why This Matters', text: rationale, hue: 'point', collapsible: true, expanded: isFirstVisit }
      : null;
    const parts = [notice, prediction, why].filter(Boolean);

    if (action || parts.length) {
      let zone = '<div class="sd-section-header">What to do</div>';
      if (action) {
        zone += `<p class="practice-detail__lead">${this.escapeHtml(action)}</p>`;
      }
      zone += this._calloutStack(parts.map((part) => takeawayBoxHtml(part)));
      body += zoneWrapHtml(zone, { id: 'practice-what-to-do' });
    }

    body += this.renderFeed(practice);
    body += '</div>';
    return header + body;
  }

  /**
   * Plate-led Finding variant — closed experiments.
   * @returns {string} HTML string
   */
  _renderFindingVariant(practice) {
    const closed = practice.completedAt || practice.createdAt;

    const header = this._renderDetailHeader(practice, [
      // The same completion pill the /discovery path detail carries — shared
      // `.tag--complete` plus the checkmark sprite — so a closed thing looks
      // closed the same way wherever you meet one. The word is "Completed",
      // not "Finding": the pill states the STATE, and what the page is was
      // already settled by the tab you arrived from and the plate's own label.
      `<span class="tag tag--complete">
        <svg width="11" height="11" viewBox="0 0 16 16" aria-hidden="true"><use href="#icon-checkmark"/></svg>
        Completed${closed ? ` · ${this.escapeHtml(this.formatDate(closed))}` : ''}
      </span>`,
      this._startedPill(practice)
    ],
    // A named close titles this page with the FINDING, not the experiment —
    // the same name the user met on the close card and on the Findings row.
    // The experiment's own title still names the page's subject, one zone
    // down. An unnamed close keeps the experiment title as the H1.
    this._findingTitle(practice));

    let body = '<div class="practice-detail__body practice-detail__standalone">';
    body += this._renderFindingResults(practice);

    // The experiment that produced it — demoted below the conclusion, but kept
    // in full: a finding read without its prediction is just an assertion.
    // Laid out as the action-led variant lays out the same zone: what was
    // done leads as open prose, and the notes ABOUT it follow as the same
    // callouts, so the two pages match. Only the labels differ, in
    // tense — the experiment is over.
    const parts = [
      this._predictionBoxOptions(practice, true),
      this._noticeBoxOptions(practice, true)
    ].filter(Boolean);

    const experimentTitle = practice.title || practice.definition?.name || '';
    if (parts.length || practice.action || experimentTitle) {
      let zone = '';
      if (practice.action) {
        zone += `<p class="practice-detail__action-text">${this.escapeHtml(practice.action)}</p>`;
      }
      zone += this._calloutStack(parts.map((part) => takeawayBoxHtml(part)));
      // Collapsible on this page only, and COLLAPSED on arrival: once the
      // experiment has closed, what it WAS is background to what it found, so
      // the page opens on the finding and folds its provenance away until
      // asked for. On the open page it is the instruction set and stays put.
      //
      // The header names it — "The experiment · <title>" — so the experiment's
      // own name survives the fold. It has to be printed SOMEWHERE on a named
      // finding: the H1 is the finding now, and the Findings row dropped its
      // provenance line on the promise that this page still names what
      // produced the finding. Collapsed, the header alone keeps that promise.
      // The zone itself opens on the action.
      body += zoneWrapHtml(
        renderDisclosure('finding-experiment', 'The experiment', zone, {
          expanded: false,
          aux: experimentTitle
        }),
        { id: 'finding-experiment-zone' }
      );
    }

    body += this.renderFeed(practice);
    body += '</div>';
    return header + body;
  }

  /**
   * The Finding's results zone: a static "What we learned" zone header, the
   * finding's lede on open ground (`.practice-detail__lead--finding`; the
   * header already marks the zone, so no plate around it), then a stack of
   * callouts — **What happened** (measure), **Carry forward** (point),
   * **What we tested** (claim). The zone never collapses, since it is what
   * the page is about.
   *
   * Which line takes the lede follows the same attribution rule on both paths:
   * synthesis leads, unquoted and unitalicised, because the italic-quote
   * register on this page means "the user's words".
   *
   *   NAMED close — the `description` is the lede: it is the finding's own
   *   statement of itself, written in the room, and the H1 above it is that
   *   finding's name. `lesson` then rides the "Carry forward" callout and
   *   `evidence` the "What happened" callout. `learned` does not render at
   *   all: with the description leading, the finding has already said itself,
   *   and `_findingConclusion`'s system-text fallbacks (report title, session
   *   summary) would put generated text beside the user's own with nothing
   *   distinguishing them.
   *
   *   LEGACY close — `lesson` takes the lede when there is one, and the quoted
   *   conclusion follows unlabelled: as the first paragraph inside "What
   *   happened" when there is evidence to seat it with, else as a neutral,
   *   unhued callout of its own. Otherwise that conclusion (with its
   *   report-title / session-summary fallbacks) is the lede itself, italic
   *   (`--quoted`) only when it is genuinely the user's `learned` line. This
   *   is the one place on the page where quote marks still appear.
   *
   * "What we tested" renders only when the experiment carried a prediction:
   * the prediction is its text, and the verdict line — dot + the band in
   * bold, under a hairline — closes the same callout, which carries the
   * `.practice-verdict--*` scope the dot's colour resolves off. An
   * unrecognised verdict has no dot and no band, so the callout ends on the
   * prediction: a colour with no meaning behind it is worse than none.
   *
   * The frontier is its own zone below ("What to try next"), and under the
   * triple gate below it carries the page's ONE in-body CTA — a documented
   * Finding-only exception to the detail pages' no-in-body-CTA rule (style
   * guide §4).
   *
   * The lede is omitted only when no report carried a description, a lesson
   * or a conclusion; evidence falls back from the takeaway's `evidence` to
   * the closing report's session summary.
   * @returns {string} HTML string
   */
  _renderFindingResults(practice) {
    const verdict = this._latestVerdict(practice);
    // The verdict line answers the prediction, so it renders only when the
    // experiment carried one — same soft slot the chat close card uses. A
    // prediction-less Finding simply ends on its body.
    const prediction = typeof practice.prediction === 'string' ? practice.prediction.trim() : '';
    const hasPrediction = prediction !== '';
    const band = hasPrediction ? (VERDICT_BANDS[verdict] || '') : '';
    const description = this._findingDescription(practice);
    // A named close has already stated itself in its description, so neither
    // `learned` nor `_findingConclusion`'s fallback chain is consulted (see
    // the header). Only a legacy close walks that chain.
    const conclusion = description ? null : this._findingConclusion(practice);
    const lesson = this._findingLesson(practice);

    const takeawayLog = this._findingTakeawayLog(practice);
    const slot = (value) => (Array.isArray(value) ? value : [])
      .filter((item) => typeof item === 'string' && item.trim())
      .join(' ')
      .trim();

    // Evidence falls back to the report's session summary on a legacy close.
    // That same summary is the LAST link of `_findingConclusion`'s fallback
    // chain, so when it already took the lede it must not repeat as the body.
    const summaryFallback = typeof this._latestLog(practice)?.sessionSummary === 'string'
      ? this._latestLog(practice).sessionSummary.trim()
      : '';
    const evidence = slot(takeawayLog?.takeawayJson?.evidence)
      || (summaryFallback !== conclusion?.text ? summaryFallback : '');
    // The ONE frontier reader — shared with the BUILD opener in
    // `handleDetailClick`, never re-derived here (see `_findingNextStep`).
    const nextStep = this._findingNextStep(practice);

    // Triple gate for the frontier door: a non-empty frontier (above), a
    // completed practice (always true here — `_renderFindingResults` only
    // renders inside the Finding variant — kept explicit to match the
    // contract), and no child yet. `allPractices` is the page's one
    // `status: 'all'` fetch, which already excludes drafts — a never-accepted
    // draft child must not permanently hide the door.
    const hasChild = this.allPractices.some((p) => p.parentPracticeId === practice.id);
    const showFrontierLink = !!nextStep && practice.status === 'completed' && !hasChild;
    // The frontier is its own zone below the plate (see the return), and it
    // reads as prose either way: the title bold, the description under it.
    // The open gate adds the build pill — the detail page's one in-body CTA
    // (style-guide §4 exception) — while the closed gate (already pursued)
    // keeps the same prose with no door. Legacy string rows carry no
    // description and stay a single bold line.
    const frontierZone = !nextStep ? '' : zoneWrapHtml(`
      <div class="sd-section-header">What to try next</div>
      <p class="practice-detail__action-text practice-detail__frontier-title">${this.escapeHtml(nextStep.title)}</p>
      ${nextStep.description
    ? `<p class="practice-detail__action-text">${this.escapeHtml(nextStep.description)}</p>`
    : ''}
      ${showFrontierLink
    ? `<button type="button" class="tag tag--session practice-detail__frontier-build" data-action="design-next-experiment" data-practice-id="${this.escapeHtml(practice.id)}">Build this experiment ${iconArrowUpRight()}</button>`
    : ''}
    `, { id: 'finding-frontier' });

    // Quote marks and the italic lede register both mean "the user's words"
    // on this page — a system-generated fallback (report title, session
    // summary) renders plain instead of borrowing that attribution.
    const conclusionHtml = conclusion
      ? (conclusion.quoted
        ? `&ldquo;${this.escapeHtml(conclusion.text)}&rdquo;`
        : this.escapeHtml(conclusion.text))
      : '';
    // Named close → the description is the lede and the lesson becomes a
    // callout. Legacy → the lesson takes the lede when there is one, else the
    // conclusion. The lede sits on open ground under the zone header, in the
    // finding's own step of the lead voice; `--quoted` keeps the italic
    // register for a quoted `learned` line and nothing else.
    const leadClass = 'practice-detail__lead practice-detail__lead--finding';
    let lede;
    if (description) {
      lede = `<p class="${leadClass}">${this.escapeHtml(description)}</p>`;
    } else if (lesson) {
      lede = `<p class="${leadClass}">${this.escapeHtml(lesson)}</p>`;
    } else if (conclusionHtml) {
      lede = `<p class="${leadClass}${conclusion.quoted ? ' practice-detail__lead--quoted' : ''}">${conclusionHtml}</p>`;
    } else {
      lede = '';
    }

    // Legacy only: when the lesson took the lede, the user's own words keep
    // their place — quoted and unlabelled — as the first paragraph inside
    // "What happened" when there is evidence to seat them with, else as a
    // neutral callout of their own (the callout form for the stack's scale,
    // but no hue and no eyebrow: the line names no beat).
    const demotedConclusion = lesson && conclusionHtml ? conclusionHtml : '';
    const happened = evidence
      ? this._calloutHtml({ label: 'What happened', text: evidence, hue: 'measure' }, demotedConclusion
        ? { prepend: `<p class="sd-takeaway__text">${demotedConclusion}</p>` }
        : {})
      : (demotedConclusion ? `<div class="sd-takeaway sd-takeaway--callout"><p class="sd-takeaway__text">${demotedConclusion}</p></div>` : '');

    // On a named close the lesson is the directive under the description —
    // plain, since it is synthesis like the description above it.
    const carryForward = description && lesson
      ? takeawayBoxHtml({ label: 'Carry forward', text: lesson, hue: 'point' })
      : '';

    // The verdict's one remaining pixel of colour — the list rows carry none,
    // so this page is where a verdict is stated at all. Decorative-redundant:
    // the band states it in words right beside the dot, hence aria-hidden. An
    // unrecognised verdict takes no dot and no band: a colour with no meaning
    // behind it is worse than none.
    // The verdict colour is scoped on the CALLOUT, not on the dot — see
    // `.practice-verdict--*` in practices.css.
    const chip = hasPrediction && verdict ? VERDICT_CHIPS[verdict] : null;
    const verdictClass = chip ? ` practice-verdict--${chip.tone}` : '';
    const dot = chip
      ? '<span class="practice-detail__verdict-dot" aria-hidden="true"></span>'
      : '';
    // The callout's closing line, not its headline: the prediction above is
    // what was tested, and this says how it came out. Rendered only when
    // there is a band to state — the prediction already sits in the text.
    const verdictLine = band
      ? `<p class="practice-detail__verdict-line">${dot}<b>${this.escapeHtml(band)}</b></p>`
      : '';
    const tested = hasPrediction
      ? this._calloutHtml(
        { label: 'What we tested', text: prediction, hue: 'claim', modifier: `practice-detail__tested${verdictClass}` },
        { append: verdictLine }
      )
      : '';

    return zoneWrapHtml(`
      <div class="sd-section-header">What we learned</div>
      ${lede}
      ${this._calloutStack([happened, carryForward, tested])}
    `, { id: 'finding-results' }) + frontierZone;
  }

  /**
   * A callout with extra, already-escaped markup seated inside it —
   * before the labelled text (`prepend`) or after it (`append`). The base
   * builder only knows label + text; the Finding's callouts carry one more
   * beat each (a demoted quoted conclusion, the verdict line).
   * @param {Object} options - {@link buildTakeawayBox} options
   * @param {Object} [extra]
   * @param {string} [extra.prepend] - Markup inserted right after the label
   * @param {string} [extra.append] - Markup inserted last
   * @returns {string} HTML string
   */
  _calloutHtml(options, { prepend = '', append = '' } = {}) {
    const box = buildTakeawayBox(options);
    if (prepend) box.querySelector('.sd-takeaway__label').insertAdjacentHTML('afterend', prepend);
    if (append) box.insertAdjacentHTML('beforeend', append);
    return box.outerHTML;
  }


  /**
   * The claim under test, or null when the experiment has no
   * prediction. Options rather than markup, so a caller can seat it in a
   * group instead of on its own plate — both detail variants group it with
   * the measurement line rather than giving it a tile of its own.
   *
   * A soft slot: an absent prediction (orientation practices,
   * prediction-less blueprint codes, backfill-skipped rows, pre-cutover
   * snapshots) omits its box rather than rendering an empty callout.
   *
   * Detail page only — the in-chat proposal card and reference panel keep
   * `buildPredictionBlock`'s fused warm plate, and the labels here are still
   * that module's constants, so the two vocabularies cannot drift.
   *
   * @param {Object} practice
   * @param {boolean} past - Closed experiment → past-tense label
   * @returns {Object|null}
   */
  _predictionBoxOptions(practice, past) {
    const prediction = typeof practice.prediction === 'string' ? practice.prediction.trim() : '';
    if (!prediction) return null;
    return {
      label: past ? PREDICTION_BLOCK_LABEL_PAST : PREDICTION_BLOCK_LABEL,
      // Unquoted: the label above it is what marks this line as the claim
      // under test, so quotes would only read as someone being quoted.
      text: prediction,
      hue: 'claim',
      modifier: 'sd-takeaway--test sd-takeaway--prediction',
      collapsible: true,
      expanded: true
    };
  }

  /**
   * The measurement line, or null when the experiment has none.
   * @returns {Object|null}
   */
  _noticeBoxOptions(practice, past) {
    const notice = typeof practice.whatToNotice === 'string' ? practice.whatToNotice.trim() : '';
    if (!notice) return null;
    return {
      label: past ? NOTICE_SUBLABEL_PAST : NOTICE_SUBLABEL,
      text: notice,
      hue: 'measure',
      modifier: 'sd-takeaway--notice',
      collapsible: true,
      expanded: true
    };
  }

  /**
   * Seat a run of callouts in one `.sd-callout-stack` (components/takeaway.css:
   * a fixed gap, no shared plate). Empty when there is nothing to seat — never
   * an empty carrier.
   * @param {Array<string>} calloutsHtml - Callout markup, in render order
   * @returns {string} HTML string
   */
  _calloutStack(calloutsHtml) {
    const parts = calloutsHtml.filter(Boolean);
    return parts.length ? `<div class="sd-callout-stack">${parts.join('')}</div>` : '';
  }

  /**
   * Render the Activity zone — one row per report AND per lineage transition
   * ("Evolved from"/"Evolved into" another experiment), newest first, all on
   * the shared `.timeline-row` component (style-guide §5). This replaces the
   * old split between "Reports" (this experiment's own check-ins) and the
   * separate "This thread" section — both already rendered on the same row
   * component, so a merge is a genuine simplification rather than a new
   * concept: one chronological feed instead of two stacked sections.
   *
   * An experiment with no reports and no lineage renders NOTHING: the
   * action-led page already says what to do and the header says the window is
   * open, so an empty zone would only restate the obvious.
   *
   * Legacy `sessionId`-less rows (retired tap-to-log) still render, so the
   * history of a pre-cutover practice stays readable.
   */
  renderFeed(practice) {
    const logEntries = (practice.logs || []).map((log) => ({ type: 'log', at: log.loggedAt, log }));
    const entries = [...logEntries, ...this._lineageEntries(practice)]
      .sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime());
    if (entries.length === 0) return '';

    const visibleCount = 3;
    const visible = entries.slice(0, visibleCount);
    const hidden = entries.slice(visibleCount);

    const renderEntry = (entry, isHidden) => entry.type === 'log'
      ? this._renderFeedRow(entry.log, isHidden)
      : this._renderLineageRow(entry, isHidden);

    const visibleHtml = visible.map((entry) => renderEntry(entry, false)).join('');
    const hiddenHtml = hidden.map((entry) => renderEntry(entry, true)).join('');
    const expandLink = hidden.length > 0
      ? `<a class="inline-link practice-feed__expand" data-feed-expand role="button" tabindex="0">
           <svg class="practice-feed__expand-icon" width="12" height="12" viewBox="0 0 12 12" fill="none">
             <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/>
           </svg>
           <span class="practice-feed__expand-text">${hidden.length} more ${hidden.length === 1 ? 'entry' : 'entries'}</span>
         </a>`
      : '';

    // A collapsible zone: the feed is the page's reference material, not the
    // thing it is about, so it must be possible to fold away. The count chip
    // is the disclosure's own (shown only while collapsed) — collapsed, the
    // chip is the only thing left saying how much is in there.
    return zoneWrapHtml(
      renderDisclosure(
        'practice-activity',
        'Activity',
        `<div class="practice-feed">${visibleHtml}${hiddenHtml}${expandLink}</div>`,
        { expanded: true, count: entries.length }
      ),
      { id: 'practice-activity-zone' }
    );
  }

  /**
   * This experiment's immediate lineage transitions, as Activity-feed entries:
   * "Evolved from" its parent (if any) and "Evolved into" each experiment that
   * names THIS one as its parent. Single-hop only — a deeper chain surfaces
   * one hop at a time as you click through each link, the same idiom the list
   * view's "Evolved from" reply-line already uses.
   *
   * Resolved entirely from `allPractices` — the page's one `status: 'all'`
   * fetch carries the newest 200 non-draft practices (parentPracticeId
   * included), so finding children costs nothing extra. Children are always
   * full rows; a parent is older than its child and can therefore fall outside
   * that window, which is why every read of the parent is guarded.
   * @returns {Array<{type: string, at: string, target: Object, verdictWord: string,
   *   headline: {text: string, quoted: boolean}|null}>}
   */
  _lineageEntries(practice) {
    const entries = [];

    if (practice.parent) {
      const parentRow = this.allPractices.find((p) => p.id === practice.parent.id);
      entries.push({
        type: 'evolved-from',
        at: practice.createdAt,
        target: practice.parent,
        verdictWord: parentRow ? this._verdictWordFor(parentRow) : '',
        headline: this._lineageHeadline(parentRow)
      });
    }

    const children = this.allPractices.filter((p) => p.parentPracticeId === practice.id);
    for (const child of children) {
      entries.push({
        type: 'evolved-into',
        at: child.createdAt,
        target: { practiceNumber: child.practiceNumber, title: child.title },
        verdictWord: this._verdictWordFor(child),
        headline: this._lineageHeadline(child)
      });
    }

    return entries;
  }

  /** Status word for a lineage row's qualifier tag — running / archived / a closing verdict. */
  _verdictWordFor(row) {
    if (row.status === 'active') return 'running';
    if (row.status === 'archived') return 'archived';
    if (row.status === 'completed') return VERDICT_WORDS[this._latestVerdict(row)] || 'closed';
    return '';
  }

  /**
   * What a lineage neighbour taught, for its Activity row — or null, silently,
   * whenever that can't be stated as settled.
   *
   * A named close is stated by its FINDING TITLE, the same name the Findings
   * row and that finding's own page carry: a lineage row is a link to the
   * neighbour, so it should say what the neighbour is called, not paraphrase
   * it a fourth way. Unquoted, like every other rendering of the title. A
   * legacy close falls back to `_findingHeadline`'s lesson-else-quoted-learned
   * line, which is all such a row has ever had.
   *
   * The gate is narrow on purpose, and each clause is load-bearing:
   *   - `row` may be undefined. An "evolved from" target is the serializer's
   *     thin 3-field snippet, and the page fetches the newest 200 practices,
   *     so a parent (always older) can fall outside the window and never
   *     resolve to a full row. Same guard the qualifier tag already takes.
   *   - `status === 'completed'` excludes a still-running neighbour outright
   *     — mid-flight thinking on an active practice is never a finding,
   *     whatever its logs carry. The DECLINED-close trap (a `takeawayJson`
   *     with `outcome: null`) on a row that HAS reached `completed` is a
   *     separate, narrower hole — that gate now lives downstream, inside
   *     `_findingTakeawayLog`, which only ever selects a log whose own
   *     `outcome` is confirmed. One gate there covers this call site and the
   *     other three surfaces that read `_findingHeadline`/`_findingLesson`.
   * @param {Object|undefined} row - Full practice row from `allPractices`
   * @returns {{text: string, quoted: boolean}|null}
   */
  _lineageHeadline(row) {
    if (!row || row.status !== 'completed') return null;
    const title = this._findingTitle(row);
    if (title) return { text: title, quoted: false };
    return this._findingHeadline(row);
  }

  /**
   * One report as a `.timeline-row` on the pattern-instances grammar: the
   * logged check sits in the left rail's icon slot under the date (style-guide
   * §5 — a semantic-state mark, the slot's sanctioned use), and the verdict /
   * kept-open status rides the title line as a filled qualifier tag
   * (`timeline-row__qualifier`, the same device as patterns' "Implied").
   */
  _renderFeedRow(log, hidden = false) {
    const hiddenClass = hidden ? ' practice-feed__row--hidden' : '';
    const isSession = !!log.sessionId;
    const dateStr = this._formatFeedDate(log.loggedAt);

    const rawTitle = typeof log.title === 'string' ? log.title.trim() : '';
    const title = rawTitle || (isSession ? 'Reported back' : 'Logged');
    const summary = isSession
      ? (log.sessionSummary || '')
      : 'Marked done — logged before reports became conversations.';

    const sessionNumber = log.session?.sessionNumber;
    const sessionLinkHtml = sessionNumber
      ? `<span class="tag tag--session" data-action="view-session" data-session-number="${this.escapeHtml(String(sessionNumber))}">
           Session ${this.escapeHtml(String(sessionNumber))} ${iconArrowUpRight()}
         </span>`
      : '';

    const word = VERDICT_WORDS[log.predictionVerdict]
      || (log.outcome === 'kept_open' ? 'kept open' : '');
    const qualifierHtml = word
      ? `<span class="tag timeline-row__qualifier">${this.escapeHtml(word)}</span>`
      : '';

    return `
      <div class="timeline-row practice-feed__row practice-feed__row--${isSession ? 'session' : 'manual'}${hiddenClass}">
        <div class="timeline-row__date-col">
          <span class="timeline-row__date">${this.escapeHtml(dateStr)}</span>
          <span class="timeline-row__icon practice-feed__check" aria-hidden="true">${this._iconCheck()}</span>
        </div>
        <div class="timeline-row__content">
          <div class="timeline-row__title-line">
            <p class="timeline-row__title">${this.escapeHtml(title)}</p>
            ${qualifierHtml}
          </div>
          ${summary ? `<p class="timeline-row__detail">${this.escapeHtml(summary)}</p>` : ''}
          ${sessionLinkHtml}
        </div>
      </div>
    `;
  }

  /** The logged-outcome check seated in a feed row's left-rail icon slot. */
  _iconCheck() {
    return `<svg width="12" height="12" viewBox="0 0 12 12" fill="none">
      <path d="M2 6.5L4.5 9L10 3" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>`;
  }

  /**
   * One lineage transition ("Evolved from"/"Evolved into" another
   * experiment) on the same `.timeline-row` grammar as a check-in report: the
   * branch glyph rides the left rail where the check-in's checkmark does, the
   * neighbor's current status is a title-line qualifier tag exactly like a
   * report's verdict tag, what a closed neighbour taught fills the detail slot
   * a report's summary uses, and a `.tag--session`-style pill (this codebase's
   * generic "outlined link elsewhere" pill, not session-specific despite the
   * name) opens that experiment's own detail panel in place.
   */
  _renderLineageRow(entry, hidden = false) {
    const hiddenClass = hidden ? ' practice-feed__row--hidden' : '';
    const dateStr = this._formatFeedDate(entry.at);
    const label = entry.type === 'evolved-from' ? 'Evolved from' : 'Evolved into';

    const target = entry.target;
    const targetTitle = (typeof target.title === 'string' && target.title.trim())
      || (typeof target.practiceNumber === 'number' ? `Experiment #${target.practiceNumber}` : 'Experiment');
    const linkHtml = typeof target.practiceNumber === 'number'
      ? `<span class="tag tag--session" data-action="view-practice" data-practice-number="${this.escapeHtml(String(target.practiceNumber))}">
           ${this.escapeHtml(targetTitle)} ${iconArrowUpRight()}
         </span>`
      : '';

    const qualifierHtml = entry.verdictWord
      ? `<span class="tag timeline-row__qualifier">${this.escapeHtml(entry.verdictWord)}</span>`
      : '';

    // What the neighbour taught, in the same detail slot a report row uses for
    // its summary. Resolved and gated in `_lineageEntries` — never re-read
    // here, so the gate lives in exactly one place. Quoting follows the card:
    // a `lesson` is synthesis and stays bare, `learned` is the user's words.
    const headlineHtml = entry.headline
      ? `<p class="timeline-row__detail">${entry.headline.quoted
        ? `&ldquo;${this.escapeHtml(entry.headline.text)}&rdquo;`
        : this.escapeHtml(entry.headline.text)}</p>`
      : '';

    return `
      <div class="timeline-row practice-feed__row practice-feed__row--lineage${hiddenClass}">
        <div class="timeline-row__date-col">
          <span class="timeline-row__date">${this.escapeHtml(dateStr)}</span>
          <span class="timeline-row__icon practice-feed__lineage-icon" aria-hidden="true">↳</span>
        </div>
        <div class="timeline-row__content">
          <div class="timeline-row__title-line">
            <p class="timeline-row__title">${this.escapeHtml(label)}</p>
            ${qualifierHtml}
          </div>
          ${headlineHtml}
          ${linkHtml}
        </div>
      </div>
    `;
  }

  _formatFeedDate(date) {
    if (!date) return '';
    const d = new Date(date);
    const now = new Date();
    const sameYear = d.getFullYear() === now.getFullYear();
    return sameYear
      ? d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
      : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  }

  // ===== STICKY STACK =====

  /**
   * (Re)bind the sticky header stack to the current scroll root. Idempotent —
   * render() replaces `container.innerHTML`, so the root is a new node every
   * time and the previous controller must be released first.
   */
  _initStickyStack() {
    this.stickyStack?.destroy();
    this.stickyStack = null;
    const root = this.container.querySelector('.explore-view--list');
    if (root) this.stickyStack = new StickyStack(root);
  }

  // ===== EVENT HANDLING =====

  attachEventListeners() {
    Tooltip.setupAll(this.container, '[data-tooltip]', { position: 'below' });

    // "New experiment" / "Build an experiment" are handled by the delegated
    // `handleActionClick` below, not wired here: `querySelector` binds the
    // FIRST match only, so the empty state's button would have gone dead
    // whenever the mobile fixed action was also on the page.
    const retryBtn = this.container.querySelector('[data-action="retry"]');
    if (retryBtn) {
      retryBtn.addEventListener('click', async () => {
        await this.loadData();
        this.render();
      });
    }

    const content = this.container.querySelector('.practice-content');
    if (content) {
      content.addEventListener('click', (e) => this.handleListClick(e));
    }

    // Delegated data-action handler (select-mode toolbar + row kebab / menu).
    // Attached once on the container; survives list re-renders.
    this.container.addEventListener('click', this.handleActionClick);

    // Right-click a practice card to open its kebab menu (same as the sidebar).
    // Stable arrow ref (class field), matching handleActionClick below — render()
    // re-runs attachEventListeners() on every reload, and a fresh inline closure
    // here would re-register on each pass (addEventListener only dedupes identical
    // function references), causing the menu to open then instantly re-close.
    this.container.addEventListener('contextmenu', this.handleRowContextMenu);
  }

  /**
   * Right-click a practice card to open its kebab menu (same as the sidebar).
   * @param {MouseEvent} event
   */
  handleRowContextMenu = (event) => {
    this._rowMenu.handleContextMenu(event);
  };

  /**
   * Delegated handler for the select-mode toolbar + per-row kebab / menu items.
   * @param {MouseEvent} event
   */
  handleActionClick = (event) => {
    const target = event.target.closest('[data-action]');
    if (!target || !this.container.contains(target)) return;
    const action = target.getAttribute('data-action');
    const id = target.getAttribute('data-practice-id');

    switch (action) {
      case 'add-practice':
        // The mobile fixed action and the empty state's button. The desktop
        // toolbar button mounts outside `container` and keeps its own listener.
        this.handleAddPractice();
        break;
      case 'open-parent-practice': {
        // Reply-line sublink — open the parent experiment instead of this
        // row. stopPropagation so the enclosing card's own detail never
        // opens instead.
        event.stopPropagation();
        if (this.selectMode) break;
        const parentNumber = parseInt(target.getAttribute('data-parent-number'), 10);
        if (Number.isFinite(parentNumber)) this._openPracticeByNumber(parentNumber);
        break;
      }
      case 'row-options':
        event.stopPropagation();
        this._rowMenu.toggle(target);
        break;
      case 'row-discuss':
        event.stopPropagation();
        this._rowMenu.close();
        if (id) this.handleDiscuss(id);
        break;
      case 'row-star':
        event.stopPropagation();
        this._rowMenu.close();
        if (id) this._runBulk('star', [id]);
        break;
      case 'row-archive':
      case 'row-restore':
        event.stopPropagation();
        this._rowMenu.close();
        if (id) this._runBulk('archive', [id]);
        break;
      case 'row-delete':
        event.stopPropagation();
        this._rowMenu.close();
        if (id) this._runBulk('delete', [id]);
        break;
      case 'select-star':
        this._runBulk('star', [...this.selectedIds]);
        break;
      case 'select-archive':
      case 'select-restore':
        this._runBulk('archive', [...this.selectedIds]);
        break;
      case 'select-delete':
        this._runBulk('delete', [...this.selectedIds]);
        break;
      case 'select-cancel':
        this._exitSelectMode();
        break;
      case 'switch-view': {
        // Segments route through the URL — see `_routeToStatus`.
        const nextView = target.getAttribute('data-view');
        if (nextView && nextView !== this.view) {
          this._routeToStatus(nextView === 'findings' ? 'completed' : 'active');
        }
        break;
      }
      default:
        break;
    }
  };

  attachDetailEventListeners() {
    if (!this._detailWrapper) return;
    this._detailWrapper.addEventListener('click', (e) => this.handleDetailClick(e));
  }

  handleListClick(e) {
    const target = e.target;
    // Clicks inside the kebab / its menu are handled by handleActionClick.
    if (target.closest('[data-action]') || target.closest('.note-row__actions')) return;
    const card = target.closest('[data-practice-id]');
    if (card) {
      const practiceId = card.dataset.practiceId;
      if (!practiceId) return;

      // Select mode: card click toggles selection instead of opening detail.
      if (this.selectMode) {
        this._toggleSelected(practiceId);
        return;
      }

      const practice = this.allPractices.find((p) => p.id === practiceId);
      const practiceNumber = practice?.practiceNumber;
      if (typeof practiceNumber !== 'number') return;
      this._openPracticeByNumber(practiceNumber);
    }
  }

  /**
   * URL-driven navigation to one practice's detail panel, by practiceNumber
   * (the stable, user-facing `?practice=` id every practice link in this
   * section uses — row clicks, a thread reply-line's parent link, and an
   * Activity-feed "Evolved from/into" row's link).
   * @param {number} practiceNumber
   */
  _openPracticeByNumber(practiceNumber) {
    // Toggle: re-opening the row already in the panel dismisses it, exactly
    // as the panel's × does. The `right-panel:closed` handler owns the rest
    // (clears selection, un-highlights the row, scrubs ?practice=).
    if (this._panelOpen && this.currentPractice?.practiceNumber === practiceNumber) {
      eventBus.emit('right-panel:close');
      return;
    }

    // From a drilled panel this section's page isn't the one showing, so the
    // current pathname is /search or /discovery — a `?practice=` there is a URL
    // nothing reads, and the click would do nothing at all. Route to the page
    // that owns the param instead. This IS an exit from the drill (the back
    // arrow to the session note ends here), which is what a lineage link means:
    // you asked for a different experiment, not for more of this one.
    if (!this.isActive) {
      page(`/practices?practice=${practiceNumber}`);
      return;
    }

    const url = new URL(window.location.href);
    url.searchParams.set('practice', String(practiceNumber));
    url.searchParams.delete('journey');

    if (this._panelOpen) {
      page.replace(url.pathname + url.search);
    } else {
      page(url.pathname + url.search);
    }
  }


  async handleDetailClick(e) {
    const target = e.target;
    const actionBtn = target.closest('[data-action]');

    if (actionBtn) {
      // The detail page carries no in-body CTA EXCEPT the Finding frontier's
      // "Build a new experiment" pill (style-guide §4 exception; the
      // "Report back" pill still lives in the panel header per style-guide
      // §6). `view-session` and `view-practice` are simple same-page
      // navigation; `design-next-experiment` launches a build session, so it
      // is awaited and guarded against an unhandled rejection (the delegated
      // listener in `attachDetailEventListeners` ignores this method's
      // returned promise).
      if (actionBtn.dataset.action === 'view-session') {
        const sessionNumber = actionBtn.dataset.sessionNumber;
        if (sessionNumber) this._openSessionFromLink(parseInt(sessionNumber, 10));
      } else if (actionBtn.dataset.action === 'view-practice') {
        // An Activity-feed lineage row's link — jumps to the neighboring
        // experiment's own detail panel, same navigation as a list row click.
        const practiceNumber = actionBtn.dataset.practiceNumber;
        if (practiceNumber) this._openPracticeByNumber(parseInt(practiceNumber, 10));
      } else if (actionBtn.dataset.action === 'design-next-experiment') {
        e.preventDefault();
        const practice = this.allPractices.find((p) => p.id === actionBtn.dataset.practiceId);
        // The SAME reader the card renders from. This opener seeds a live
        // build session, so a second parse of `takeawayJson.next` here is how
        // a structured frontier becomes "[object Object]" in a prompt.
        const nextStep = practice ? this._findingNextStep(practice) : null;
        if (!nextStep) return; // stale/malformed id or vanished frontier: no-op
        try {
          await launchPracticeBuild({
            findingAction: { practiceId: practice.id },
            // Title only: the description is the card's second row, not the
            // opener's quoted line, and the handoff context carries the fuller
            // text server-side (`readFinding().next`).
            opener: `Let's build the next experiment from what this one taught me: "${nextStep.title}"`,
            source: 'finding_frontier'
          });
        } catch (error) {
          console.error('[PracticesSection] Failed to launch finding-frontier build', error);
        }
      }
      return;
    }

    const feedExpand = target.closest('[data-feed-expand]');
    if (feedExpand) {
      const feed = feedExpand.closest('.practice-feed');
      if (feed) {
        const willExpand = !feed.classList.contains('is-expanded');
        feed.classList.toggle('is-expanded', willExpand);
        const textEl = feedExpand.querySelector('.practice-feed__expand-text');
        if (textEl) {
          if (willExpand) {
            textEl.textContent = 'Show less';
          } else {
            const hiddenCount = feed.querySelectorAll('.practice-feed__row--hidden').length;
            textEl.textContent = `${hiddenCount} more ${hiddenCount === 1 ? 'entry' : 'entries'}`;
          }
        }
      }
      return;
    }

    const sessionTag = target.closest('.tag--session');
    if (sessionTag) {
      const sessionNumber = sessionTag.dataset.sessionNumber;
      if (sessionNumber) {
        this._openSessionFromLink(parseInt(sessionNumber, 10));
      }
      return;
    }

    const takeawayToggle = target.closest('[data-takeaway-toggle]');
    if (takeawayToggle) {
      toggleTakeawayBox(takeawayToggle);
    }
  }

  _openSessionFromLink(sessionNumber) {
    return openSessionInPanel(sessionNumber);
  }

  showPageInfo(activeTab = 'overview') {
    const content = buildPageInfoPanel({
      title: 'About Experiments',
      intro: `Experiments are small, concrete tries — each one paired with a
        prediction and a single window to run it in, so insight turns into
        something you actually find out.`,
      tabs: PracticesSection.PAGE_INFO_TABS,
      activeTab
    });

    // Reading the full page explanation retires the inline "What are these?"
    // hint on chat cards — otherwise the user is offered a primer they just
    // read. The hint key is internal naming and stays `practices`.
    window.helpHintService?.markDismissed?.('practices');

    this._panelOpen = true;
    eventBus.emit('right-panel:open', {
      content,
      // Literal, not derived from the section name: PageManager keys off the
      // `-info` suffix, and the three page-info contentTypes don't share a
      // naming rule (see components/pageInfoPanel.js).
      contentType: 'practice-info'
    });
  }

  async handleAddPractice() {
    // Cold build — no handoff context, so the strategy asks what to work on.
    // Shares one launcher with the guide `action` atom's "Build experiment".
    await launchPracticeBuild();
  }

  async markPracticeViewed(practice) {
    if (!practice || practice.viewedAt) return;

    const result = await API.markPracticeViewed(practice.id);
    if (result?.viewedAt) {
      practice.viewedAt = result.viewedAt;
      this.updateContentOnly();
      // Confirmed-success only — so a practice viewed natively here updates
      // its sidebar Recents/Starred row live, mirroring how SessionsSection/
      // PartnerNotesSection/DiscoverySection/InsightsSection already emit.
      emitContentViewed('practice', practice.id, true);
    }
  }

  /**
   * Run a detail-header Options action through the same bulk paths as the row
   * kebab / select toolbar (single-id). archive/restore/delete change status
   * and close the panel (handled inside _runBulk). Star lives in the panel
   * header now (see showPracticeDetail's starButton), not this dropdown.
   * Direction is derived per row inside `_runBulk`, so both entry points map
   * onto the one `archive` path.
   * @param {'archive'|'restore'|'delete'} action
   * @param {string} practiceId
   */
  async _handleDetailOption(action, practiceId) {
    // archive | restore → the 'archive' bulk path (direction derived per row)
    await this._runBulk(action === 'delete' ? 'delete' : 'archive', [practiceId]);
  }

  /**
   * Open the report conversation for one experiment — the "Report back" CTA.
   */
  handleDiscuss(practiceId) {
    const practice = this.allPractices.find((p) => p.id === practiceId);
    if (!practice) {
      console.error('[PracticesSection] Practice not found for discuss:', practiceId);
      return;
    }

    eventBus.emit('navigate', {
      page: 'chat',
      mode: 'TREATMENT',
      subMode: 'checkin',
      practiceId
    });
  }

  /**
   * Apply URL parameters — drives view state (?status=) and panel open
   * (?practice=) from URL state. Accepts legacy ?journey= as a read-only
   * fallback so old bookmarks still resolve.
   *
   * No refetch on a view or filter change: one `status: 'all'` load backs both
   * segments and both sides of the Show filter. Either change repaints the
   * whole page — the bar's count noun and its applied-filter state move with
   * the body, and the bar is rebuilt by `_mountFilterBar`, not patched.
   */
  async applyURLParams(params) {
    const next = STATUS_TO_STATE[params.status] || STATUS_TO_STATE.active;
    const viewChanged = next.view !== this.view;
    const filterChanged = next.listFilter !== this.listFilter;
    if (viewChanged || filterChanged) {
      this._resetSelectMode();
      this.view = next.view;
      this.listFilter = next.listFilter;
      this.render();
    }

    let practiceParam = params.practice;
    if (!practiceParam && params.journey) {
      console.warn('[PracticesSection] ?journey= URL param is deprecated; use ?practice= instead');
      practiceParam = params.journey;
    }

    if (practiceParam) {
      const practiceNumber = parseInt(practiceParam, 10);
      if (!Number.isFinite(practiceNumber)) return;

      if (this._panelOpen && this.currentPractice?.practiceNumber === practiceNumber) return;

      const practice = this.allPractices.find((p) => p.practiceNumber === practiceNumber);
      if (practice) {
        this.showPracticeDetail(practice.id);
      } else {
        const url = new URL(window.location.href);
        url.searchParams.delete('practice');
        url.searchParams.delete('journey');
        page.replace(url.pathname + (url.search ? url.search : ''));
      }
    } else if (this._panelOpen && this.currentPractice) {
      eventBus.emit('right-panel:close');
    }
  }

  updateContentOnly() {
    // The panel this section builds can now be driven from another page (a
    // session note drilling into an experiment), where this page isn't rendered
    // and `.practice-content` doesn't exist. Re-rendering a list nobody is
    // looking at is never the point of these calls — the state mutation before
    // them is.
    if (!this.isActive) return;
    this._rowMenu.close();
    const contentContainer = this.container.querySelector('.practice-content');
    if (contentContainer) {
      contentContainer.innerHTML = this.renderListContent();
    }
    this._syncNavBar();
    if (this.filterBar) {
      this.filterBar.setCount(this.getVisiblePractices().length);
    }
  }

  // ===== SELECT MODE + BULK ACTIONS =====

  /** The currently-selected practices (from the loaded list). */
  _selectedPractices() {
    return this.allPractices.filter((p) => this.selectedIds.has(p.id));
  }

  /** Clear select-mode state without re-rendering. */
  _resetSelectMode() {
    this.selectMode = false;
    this.selectedIds = new Set();
  }

  /**
   * Enter select mode: swap bar for toolbar, re-render rows with checkboxes.
   * Nothing to reset on the way in — the Show filter is URL state the toolbar
   * swap must not silently rewrite, and Findings has no filter at all.
   */
  _enterSelectMode() {
    if (this.selectMode) return;
    this.selectMode = true;
    this.selectedIds = new Set();
    this._mountFilterBar();
    this.updateContentOnly();
  }

  /** Exit select mode: clear selection, restore the filter bar. */
  _exitSelectMode() {
    this._resetSelectMode();
    this._mountFilterBar();
    this.updateContentOnly();
  }

  /**
   * Toggle one practice in the selection and refresh toolbar + rows.
   * @param {string} id - Practice id
   */
  _toggleSelected(id) {
    if (this.selectedIds.has(id)) this.selectedIds.delete(id);
    else this.selectedIds.add(id);
    this._renderSelectToolbar();
    this.updateContentOnly();
  }

  /**
   * Run a bulk action over practice ids. Star flips the marker in place (no
   * view change). Archive/Restore/Delete change status, so rows move between
   * the segments and the two sides of the Show filter — after those, refetch.
   * Delete confirms first.
   *
   * `archive` is one path in both directions; see the per-row derivation
   * below. Delete confirmation copy is experiment-voiced.
   * @param {'star'|'archive'|'delete'} action
   * @param {string[]} ids - Practice ids
   */
  async _runBulk(action, ids) {
    if (this._actionInFlight || ids.length === 0) return;
    const idSet = new Set(ids);
    const practices = this.allPractices.filter((p) => idSet.has(p.id));
    if (practices.length === 0) return;

    if (action === 'delete') {
      const n = practices.length;
      const confirmed = await modalService.confirm({
        title: `Delete ${n === 1 ? 'experiment' : `${n} experiments`}`,
        message: `Permanently delete ${n === 1 ? 'this experiment' : `${n} experiments`}? This also deletes ${n === 1 ? 'its' : 'their'} reports.`,
        confirmText: 'Delete',
        cancelText: 'Cancel',
        variant: 'danger'
      });
      if (!confirmed || !this.isActive) return;
    }

    this._actionInFlight = true;
    if (this.selectMode) this._renderSelectToolbar();

    try {
      if (action === 'star') {
        const starred = !practices.every((p) => p.starredAt);
        const res = await API.starPractices(ids, starred);
        if (res?.success !== false) {
          const now = new Date().toISOString();
          // Apply-local-FIRST, then emit per item (before the `!isActive`
          // guard below, R2-Warning-1-style — other surfaces still sync when
          // the user navigated away mid-flight), so this section's own
          // listener would self-skip if it ever subscribed back.
          for (const p of practices) {
            p.starredAt = starred ? now : null;
            emitContentStarred('practice', p.id, starred);
          }
        }
        if (!this.isActive) return;
        // Star doesn't change tab membership — update in place.
        if (this.selectMode) this._exitSelectMode();
        else this.updateContentOnly();
        return;
      }

      // Archive / Restore / Delete: change status → refetch.
      //
      // Direction is derived PER ROW from its own status, not from a view: a
      // selection can outlive a Show-filter flip, and the
      // `statusFilter === 'archived'` segment this used to key off no longer
      // exists. Archive routes through `/archive`, which
      // is where `closeReason` is written — server-side, exactly once.
      // Findings can be neither archived nor restored, so they are dropped.
      const targets = action === 'delete'
        ? practices
        : practices.filter((p) => p.status === 'active' || p.status === 'archived');
      if (targets.length === 0) return;

      // Per-item, per-direction outcome tallies. These used to be
      // `.catch(() => null)`, which swallowed every failure without so much as
      // a log — a failed archive showed the user nothing at all.
      let succeeded = 0;
      let failed = 0;
      const archiveTally = { ok: 0, failed: 0 };
      const restoreTally = { ok: 0, failed: 0 };
      const jobs = targets.map((p) => {
        if (action === 'delete') {
          return API.deletePractice(p.id).then((res) => {
            if (res?.deleted) {
              succeeded++;
              emitContentDeleted('practice', p.id);
            } else {
              failed++;
            }
          }).catch((err) => {
            failed++;
            console.error('[PracticesSection] Delete failed:', p.id, err?.message || err);
          });
        }
        const archived = p.status !== 'archived';
        const tally = archived ? archiveTally : restoreTally;
        // archivePractice/restorePractice THROW on failure and resolve a bare
        // practice object (no `success`, no `count`) — so for this type, not
        // throwing is the success signal.
        return (archived
          ? API.archivePractice(p.id)
          : API.restorePractice(p.id)).then(() => {
            tally.ok++;
            emitContentArchived('practice', p.id, archived);
          }).catch((err) => {
            tally.failed++;
            console.error('[PracticesSection] Archive failed:', p.id, err?.message || err);
          });
      });
      await Promise.allSettled(jobs);
      if (!this.isActive) return;

      if (action === 'delete') {
        if (failed > 0) {
          window.notificationManager?.showError(
            `Couldn't delete ${failed === 1 ? 'that' : 'those'}. Please try again.`
          );
        }
      } else {
        // A mixed batch reports each direction on its own terms — one toast
        // can't say "2 archived" and "1 restored" at the same time.
        if (archiveTally.ok > 0) notifyArchived({ count: archiveTally.ok, type: 'practice', archived: true });
        if (restoreTally.ok > 0) notifyArchived({ count: restoreTally.ok, type: 'practice', archived: false });
        if (archiveTally.failed > 0) notifyArchiveFailed({ count: archiveTally.failed, type: 'practice', archived: true });
        if (restoreTally.failed > 0) notifyArchiveFailed({ count: restoreTally.failed, type: 'practice', archived: false });
      }

      // Close the open panel if its practice was acted on.
      if (this._panelOpen && this.currentPractice && idSet.has(this.currentPractice.id)) {
        eventBus.emit('right-panel:close');
      }

      await this.loadData();
      if (!this.isActive) return;
      if (this.selectMode) this._exitSelectMode();
      else this.updateContentOnly();
    } catch (error) {
      console.error('[PracticesSection] Bulk action failed:', error);
      window.notificationManager?.showError('Something went wrong. Please try again.');
    } finally {
      this._actionInFlight = false;
    }
  }

  // ===== UTILITIES =====

  formatDate(date, prefix = null) {
    if (!date) return prefix ? `${prefix} -` : '-';
    const d = new Date(date);
    const formatted = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    return prefix ? `${prefix} - ${formatted}` : formatted;
  }

  escapeHtml(text) {
    return escapeHtml(text);
  }
}
